import React, { useState, useEffect } from 'react';
import './Cards.css';

function Cards() {
  // Six phones (img1,img2,img3,img1,img2,img3) live on six fixed integer
  // SLOTS (-3..2) spaced evenly along one linear arc that only ever spans
  // the FRONT-FACING angle range — never a full circle, so there is no
  // "back half" to hide behind overflow:hidden. Slot 0 is the arc's exact
  // center (biggest, highest, sharpest). Only three phones — the center
  // slot and its immediate left/right neighbor (|slot| <= 1) — are kept
  // fully opaque; everything beyond that (|slot| > 1) fades out via
  // opacity so only 3 phones are ever visible on screen at once, with the
  // rest waiting invisibly in the fade before sliding back in.
  //
  // Motion: each phone's slot decreases by 1 every tick (sliding the
  // whole arc left, like a coverflow), wrapped back into the [-3, 3)
  // range with +6 when it exits the left edge. Because opacity already
  // reaches 0 well before |slot| hits the wrap boundary (3), the phone
  // that "teleports" from the far left back to the far right does so
  // while fully invisible, so the wrap itself is never seen.
  const HOLD_MS = 1200;
  const ROTATE_MS = 850;
  const SLOT_COUNT = 6;
  const ANGLE_PER_SLOT = 30; // degrees
  const [rotationStep, setRotationStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setRotationStep((s) => s + 1);
    }, HOLD_MS + ROTATE_MS);
    return () => clearInterval(interval);
  }, []);
  // JS only computes resolution-independent ratios; the actual pixel
  // radii live in CSS (--orbit-radius-x/-y, overridden per breakpoint via
  // media query) so responsiveness needs no resize listener.
  const phoneStyle = (baseSlot) => {
    const rawSlot = baseSlot - rotationStep;
    // Wrap into [-3, 3), integer slots only.
    const wrapped = ((((rawSlot + 3) % SLOT_COUNT) + SLOT_COUNT) % SLOT_COUNT) - 3;
    const angleDeg = wrapped * ANGLE_PER_SLOT;
    const angleRad = (angleDeg * Math.PI) / 180;
    const xRatio = Math.sin(angleRad);
    // The arc dips gently away from center rather than rising into a
    // hood — 0 at the center slot, growing (downward) toward the edges.
    const yRatio = 1 - Math.cos(angleRad);
    const absSlot = Math.abs(wrapped);
    // Scale eases down from 1 at center to ~0.8 at the outer slots.
    const scale = 1 - 0.08 * absSlot;
    // Rotation follows the same arc angle position is derived from, so
    // each phone visually "leans into" its spot on the arc.
    const rotateDeg = angleDeg * 0.4;
    // Center slot always renders above the outer ones.
    const zIndex = Math.round((3 - absSlot) * 40) + 50;
    // Fully visible only for the center + one neighbor each side
    // (|slot| <= 1 => exactly 3 phones on screen); fades to 0 by
    // |slot| = 2, well clear of the slot-3 wrap boundary.
    const opacity = absSlot <= 1 ? 1 : Math.max(0, 1 - (absSlot - 1));
    return {
      '--x-ratio': xRatio,
      '--y-ratio': yRatio,
      '--phone-scale': scale,
      '--phone-rotate': `${rotateDeg}deg`,
      opacity,
      zIndex,
    };
  };

  // "Your card activity, in real time" — a vertical stack of the 4 real
  // transaction-card screenshots (activity1-4.png) stepping through on a
  // timer (translateY by one card-step, then wraps via a duplicated track
  // so the loop is seamless), with a glow background and top/bottom fade
  // masks. Motion architecture adapted from ScallopTransactionAnimation.jsx.
  const scallopTxItems = [
    { id: 'michel', img: '/framerusercontent.com/animation/activity1.png' },
    { id: 'barclays', img: '/framerusercontent.com/animation/activity2.png' },
    { id: 'swap', img: '/framerusercontent.com/animation/activity3.png' },
    { id: 'dwight', img: '/framerusercontent.com/animation/activity4.png' },
  ];
  const TX_STEP_MS = 730;
  const TX_TRANSITION_MS = 420;
  const [txStep, setTxStep] = useState(0);
  const [txInstant, setTxInstant] = useState(false);
  // "Upgrade your experience" metal-card stack: tap/click (or Enter/Space —
  // the component is keyboard-focusable) slides the black metal card out
  // from behind Front (see .metal-card-out in Cards.css). One state drives
  // every responsive copy so desktop/tablet/mobile stay in sync.
  const [metalCardOut, setMetalCardOut] = useState(false);
  const toggleMetalCard = () => setMetalCardOut((v) => !v);
  const onMetalCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMetalCard();
    }
  };
  const txTrackItems = [...scallopTxItems, ...scallopTxItems];
  useEffect(() => {
    let timeout;
    const interval = setInterval(() => {
      timeout = setTimeout(() => {
        setTxStep((value) => value + 1);
      }, TX_TRANSITION_MS);
    }, TX_STEP_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);
  // txStep never wraps mid-scroll (that would snap the track backward).
  // Once it reaches the end of the duplicated track, the track is visually
  // identical to txStep=0 — so reset there with the transition switched
  // off for one frame, then switch it back on, so the reset itself is
  // never seen and the motion always reads as continuing forward.
  useEffect(() => {
    if (txStep !== scallopTxItems.length) return;
    const resetTimeout = setTimeout(() => {
      setTxInstant(true);
      setTxStep(0);
    }, TX_TRANSITION_MS);
    return () => clearTimeout(resetTimeout);
  }, [txStep, scallopTxItems.length]);
  useEffect(() => {
    if (!txInstant) return;
    const raf = requestAnimationFrame(() => setTxInstant(false));
    return () => cancelAnimationFrame(raf);
  }, [txInstant]);

  // "Create cards for anything" virtual-card strip — pause-then-move stepping:
  // 1s hold with a card on the phone screen, then a 1s slide to the next
  // card, on every breakpoint. One state drives all three responsive copies
  // so desktop/tablet/phone stay in sync. Each UL holds 4 copies of 5 real
  // cards, so after 5 steps the track sits exactly one copy over (-25%),
  // visually identical to step 0 — reset there instantly (same trick as
  // the tx track above) so the loop never snaps backward.
  const VCARD_PAUSE_MS = 1000;
  const VCARD_SLIDE_MS = 1000;
  const VCARD_COPY_LEN = 5;
  const [vcardStep, setVcardStep] = useState(0);
  const [vcardInstant, setVcardInstant] = useState(false);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const interval = setInterval(() => {
      setVcardStep((value) => value + 1);
    }, VCARD_PAUSE_MS + VCARD_SLIDE_MS);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (vcardStep !== VCARD_COPY_LEN) return;
    const resetTimeout = setTimeout(() => {
      setVcardInstant(true);
      setVcardStep(0);
    }, VCARD_SLIDE_MS);
    return () => clearTimeout(resetTimeout);
  }, [vcardStep]);
  useEffect(() => {
    if (!vcardInstant) return;
    const raf = requestAnimationFrame(() => setVcardInstant(false));
    return () => cancelAnimationFrame(raf);
  }, [vcardInstant]);
  // Center each held card mid-phone: measure the visible copy's phone-base
  // center vs its marquee viewport left edge, and hold that as
  // --vcard-hold-offset, so step positions land card centers on the phone
  // screen (card center = i*step + cardW/2 == phone center). Re-measured
  // on resize; falls back to 0 (previous behavior) until measurable.
  useEffect(() => {
    const measure = () => {
      const viewports = Array.from(
        document.querySelectorAll('.page-cards .cards-s90')
      ).filter((el) => el.offsetWidth > 0);
      const phones = Array.from(
        document.querySelectorAll('.page-cards .framer-5puxpo')
      ).filter((el) => el.offsetWidth > 0);
      if (!viewports.length || !phones.length) return;
      const vRect = viewports[0].getBoundingClientRect();
      let best = null;
      let bestDist = Infinity;
      phones.forEach((phone) => {
        const pRect = phone.getBoundingClientRect();
        const dist = Math.abs(
          pRect.top + pRect.height / 2 - (vRect.top + vRect.height / 2)
        );
        if (dist < bestDist) {
          bestDist = dist;
          best = pRect;
        }
      });
      if (!best) return;
      const phoneCenterX = best.left + best.width / 2 - vRect.left;
      const cardW = window.matchMedia?.('(max-width: 809.98px)').matches
        ? 170
        : 250;
      const offset = Math.round(phoneCenterX - cardW / 2);
      document
        .querySelectorAll('.page-cards .vcard-marquee-track')
        .forEach((track) =>
          track.style.setProperty('--vcard-hold-offset', `${offset}px`)
        );
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Framer's marquee scrolled infinitely via a JS rAF loop that duplicated
  // this list at runtime for a seamless loop — stripped along with all
  // other scripts. Defining the tag list once and rendering it twice lets a
  // plain CSS keyframe animation (see .cards-marquee-track in Cards.css)
  // scroll a track that's exactly double the visible width by exactly 50%,
  // looping seamlessly without duplicating this JSX by hand.
  const cardTagItems = (
    <>
      <li className="cards-s43">
        <div className="framer-17ol3hj-container cards-s44">
          <div
            className="framer-GBOWx framer-QhPas framer-6mjyg6 framer-v-6mjyg6 cards-s45"
            data-border="true"
            data-framer-name="Tag"
          >
            <div className="framer-1h8ksqg" data-framer-name="Icon Container">
              <div
                data-framer-background-image-wrapper="true"
                className="cards-s28"
              >
                <img
                  decoding="async"
                  width="22"
                  height="22"
                  src="/framerusercontent.com/images/zYAgt4gQWJqvjKgB2XJomPVyNjw.svg"
                  alt=""
                  className="cards-s29"
                />
              </div>
            </div>
            <div
              className="framer-13bhad4 cards-s46 reveal"
              data-framer-name="Text"
              data-framer-component-type="RichTextContainer"
            >
              <p
                className="framer-text framer-styles-preset-sogsmk cards-s47"
                data-styles-preset="lxpP0Ww7O"
              >
                Freeze
              </p>
            </div>
          </div>
        </div>
      </li>
      <li className="cards-s48">
        <div className="framer-1jz57g4-container cards-s49">
          <div
            className="framer-GBOWx framer-QhPas framer-6mjyg6 framer-v-6mjyg6 cards-s45"
            data-border="true"
            data-framer-name="Tag"
          >
            <div className="framer-1h8ksqg" data-framer-name="Icon Container">
              <div
                data-framer-background-image-wrapper="true"
                className="cards-s28"
              >
                <img
                  decoding="async"
                  width="22"
                  height="22"
                  src="/framerusercontent.com/images/gf5BCCdpIP8dT4WY3nwmXMUZVrU.svg"
                  alt=""
                  className="cards-s29"
                />
              </div>
            </div>
            <div
              className="framer-13bhad4 cards-s46 reveal"
              data-framer-name="Text"
              data-framer-component-type="RichTextContainer"
            >
              <p
                className="framer-text framer-styles-preset-sogsmk cards-s47"
                data-styles-preset="lxpP0Ww7O"
              >
                Block
              </p>
            </div>
          </div>
        </div>
      </li>
      <li className="cards-s50">
        <div className="framer-fzgism-container cards-s51">
          <div
            className="framer-GBOWx framer-QhPas framer-6mjyg6 framer-v-6mjyg6 cards-s45"
            data-border="true"
            data-framer-name="Tag"
          >
            <div className="framer-1h8ksqg" data-framer-name="Icon Container">
              <div
                data-framer-background-image-wrapper="true"
                className="cards-s28"
              >
                <img
                  decoding="async"
                  width="22"
                  height="22"
                  src="/framerusercontent.com/images/MnLWLH9pnP5EsDqQuIz8MioD54.svg"
                  alt=""
                  className="cards-s29"
                />
              </div>
            </div>
            <div
              className="framer-13bhad4 cards-s46 reveal"
              data-framer-name="Text"
              data-framer-component-type="RichTextContainer"
            >
              <p
                className="framer-text framer-styles-preset-sogsmk cards-s47"
                data-styles-preset="lxpP0Ww7O"
              >
                Swipe
              </p>
            </div>
          </div>
        </div>
      </li>
      <li className="cards-s52">
        <div className="framer-1oizsvz-container cards-s53">
          <div
            className="framer-GBOWx framer-QhPas framer-6mjyg6 framer-v-6mjyg6 cards-s45"
            data-border="true"
            data-framer-name="Tag"
          >
            <div className="framer-1h8ksqg" data-framer-name="Icon Container">
              <div
                data-framer-background-image-wrapper="true"
                className="cards-s28"
              >
                <img
                  decoding="async"
                  width="22"
                  height="22"
                  src="/framerusercontent.com/images/jr7Re2CkAdmRRhVgs774A1Sleg.svg"
                  alt=""
                  className="cards-s29"
                />
              </div>
            </div>
            <div
              className="framer-13bhad4 cards-s46 reveal"
              data-framer-name="Text"
              data-framer-component-type="RichTextContainer"
            >
              <p
                className="framer-text framer-styles-preset-sogsmk cards-s47"
                data-styles-preset="lxpP0Ww7O"
              >
                Modify
              </p>
            </div>
          </div>
        </div>
      </li>
      <li className="cards-s54">
        <div className="framer-1qkrg36-container cards-s55">
          <div
            className="framer-GBOWx framer-QhPas framer-6mjyg6 framer-v-6mjyg6 cards-s45"
            data-border="true"
            data-framer-name="Tag"
          >
            <div className="framer-1h8ksqg" data-framer-name="Icon Container">
              <div
                data-framer-background-image-wrapper="true"
                className="cards-s28"
              >
                <img
                  decoding="async"
                  width="22"
                  height="22"
                  src="/framerusercontent.com/images/u8Bzum2FwzYrJkET1PmmJHvk.svg"
                  alt=""
                  className="cards-s29"
                />
              </div>
            </div>
            <div
              className="framer-13bhad4 cards-s46 reveal"
              data-framer-name="Text"
              data-framer-component-type="RichTextContainer"
            >
              <p
                className="framer-text framer-styles-preset-sogsmk cards-s47"
                data-styles-preset="lxpP0Ww7O"
              >
                Analyse
              </p>
            </div>
          </div>
        </div>
      </li>
    </>
  );

  return (
    <div className="page-cards">
      <div
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        <>
          <svg width="48" height="48" id="svg-539090904_324">
            <g fill="#fff">
              <path d="M15 15.436V12a9 9 0 1 1 18 0v3.436a18.082 18.082 0 0 1 2 1.348V12a11 11 0 0 0-22 0v4.784a18.082 18.082 0 0 1 2-1.348Z" />
              <path d="M24 15a16 16 0 1 0 16 16 16.019 16.019 0 0 0-16-16Zm1 18.9V38a1 1 0 0 1-2 0v-4.1a5 5 0 1 1 2 0Z" />
            </g>
          </svg>
          <svg
            width="32"
            height="32"
            viewBox="0 0 256 256"
            id="svg-1208994263_271"
          >
            <path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24Zm45.66 85.66-56 56a8 8 0 0 1-11.32 0l-24-24a8 8 0 0 1 11.32-11.32L112 148.69l50.34-50.35a8 8 0 0 1 11.32 11.32Z" />
          </svg>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            id="svg-484590915_1528"
          >
            <mask
              id="svg-484590915_1528_mask0_214_14103"
              style={{ maskType: 'alpha' }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="16"
              height="16"
            >
              <path
                d="M15.2411 7.77411C15.2411 11.8651 11.9247 15.1815 7.83366 15.1815C3.74266 15.1815 0.426254 11.8651 0.426254 7.77411C0.426254 3.68311 3.74266 0.366699 7.83366 0.366699C11.9247 0.366699 15.2411 3.68311 15.2411 7.77411Z"
                fill="#C4C4C4"
              />
            </mask>
            <g mask="url(#svg-484590915_1528_mask0_214_14103)"></g>
            <path
              d="M10.8485 5.03449C10.7968 4.91005 10.6979 4.81117 10.5735 4.75949C10.5122 4.7334 10.4465 4.71955 10.3799 4.71875H5.28734C5.15228 4.71875 5.02275 4.7724 4.92724 4.86791C4.83174 4.96341 4.77808 5.09295 4.77808 5.22801C4.77808 5.36307 4.83174 5.49261 4.92724 5.58811C5.02275 5.68361 5.15228 5.73727 5.28734 5.73727H9.15262L4.92577 9.95903C4.87804 10.0064 4.84015 10.0627 4.8143 10.1248C4.78844 10.1868 4.77513 10.2534 4.77513 10.3206C4.77513 10.3878 4.78844 10.4544 4.8143 10.5165C4.84015 10.5785 4.87804 10.6348 4.92577 10.6822C4.97311 10.7299 5.02944 10.7678 5.09149 10.7936C5.15355 10.8195 5.22012 10.8328 5.28734 10.8328C5.35457 10.8328 5.42113 10.8195 5.48319 10.7936C5.54525 10.7678 5.60158 10.7299 5.64892 10.6822L9.87068 6.45532V10.3206C9.87068 10.4557 9.92433 10.5852 10.0198 10.6807C10.1153 10.7762 10.2449 10.8299 10.3799 10.8299C10.515 10.8299 10.6445 10.7762 10.74 10.6807C10.8355 10.5852 10.8892 10.4557 10.8892 10.3206V5.22801C10.8884 5.16146 10.8745 5.09571 10.8485 5.03449V5.03449Z"
              fill="#FF6E66"
            />
          </svg>
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            id="svg86067174_1236"
          >
            <path
              d="M26.2558 44.7319C27.3695 43.0923 28.4316 41.5146 29.4989 39.942C29.7103 39.6275 29.8598 39.4058 30.1898 39.0552C30.4321 38.7922 30.958 38.2457 31.1849 38.5654C31.3447 38.8489 31.4221 39.0706 31.1076 39.6533C30.2878 41.1537 29.2514 42.52 28.2305 43.8812C27.3282 45.0825 26.6219 45.9848 25.7608 47.0006C25.3586 47.4749 24.9668 47.4337 24.6162 46.9799C24.3017 46.5726 23.9717 46.1601 23.7397 45.7012C22.8941 44.0513 22.0949 42.3859 21.2751 40.7206C21.172 40.5143 21.0792 40.3596 20.9915 40.0967C20.8884 39.777 20.7956 39.5502 21.0792 39.3903C21.3473 39.2356 21.6463 39.5553 21.7649 39.7461C22.791 41.3187 24.8534 44.5979 25.0029 44.7474C25.2452 43.6388 25.4927 42.618 25.6835 41.5816C26.9776 34.3581 27.7459 27.1036 26.7817 19.7563C26.0186 13.9404 24.5801 8.31518 22.2754 2.91686C22.1413 2.59719 22.033 2.26205 21.9351 1.93723C21.7959 1.49897 21.801 1.24632 22.0227 1.15867C22.461 1.0504 22.6311 1.32366 22.7858 1.5299C23.1003 1.93723 23.3323 2.41673 23.5592 2.88077C26.529 9.11952 27.9624 15.7501 28.5296 22.5973C29.1122 29.6249 28.1016 36.4927 26.5033 43.2986C26.4053 43.6543 26.2455 44.5566 26.2558 44.7319Z"
              fill="black"
            />
          </svg>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            id="svg-458407791_470"
          >
            <g opacity="0.5">
              <path
                d="M12.2194 5.21967C12.5122 4.92678 12.987 4.92678 13.2799 5.21967C13.5728 5.51256 13.5728 5.98732 13.2799 6.28022L8.10998 11.4501C7.84392 11.7162 7.42225 11.7435 7.12365 11.5146L4.29357 9.34467C3.96489 9.09263 3.90288 8.62259 4.1549 8.29389C4.40694 7.96521 4.87698 7.90319 5.20568 8.15522L7.5133 9.92475L12.2194 5.21967Z"
                fill="black"
              />
            </g>
          </svg>
          <svg
            width="196"
            height="196"
            viewBox="0 0 196 196"
            fill="none"
            id="svg829552244_2744"
          >
            <path
              d="M97.8955 0C151.962 0 195.791 43.8294 195.791 97.8955C195.791 151.962 151.962 195.791 97.8955 195.791C43.8293 195.791 7.01047e-05 151.962 0 97.8955C0 43.8294 43.8293 9.07232e-05 97.8955 0ZM74.2256 20.4414C73.6144 20.1009 73.2324 20.2321 72.5449 20.4268C51.566 26.407 33.8709 40.5696 23.4395 59.7275C12.874 78.7275 10.2446 101.13 16.1191 122.061C22.0941 143.379 36.4269 161.381 55.8672 171.981C66.0066 177.534 79.9001 181.378 91.5732 180.82C101.346 180.522 110.07 177.38 116.868 170.165C122.7 163.975 125.738 155.306 125.277 146.796C124.308 128.907 112.099 117.666 100.058 106.201C87.3897 94.14 72.0285 83.0712 64.0645 67.2598C62.778 64.7058 61.7885 61.9377 61.085 59.1904C60.8065 58.103 60.2419 54.666 59.9346 53.999L59.7051 54.0215C59.0049 55.0027 58.3648 57.1395 57.9355 58.3037C55.316 65.4067 54.4393 72.8772 55.5225 80.3594C57.6592 95.1261 69.0327 105.589 79.5146 115.13C83.8448 119.071 88.4182 122.904 92.7109 126.896C100.355 134.049 109.29 142.572 108.327 153.978C108.091 157.107 107.013 160.115 105.207 162.682C100.977 168.643 94.8094 169.616 88.0684 169.328C77.2665 168.867 67.3603 164.789 58.7373 158.41C53.908 154.827 49.5487 150.65 45.7637 145.978C33.3815 129.929 28.2164 111.568 30.8193 91.3652C33.3117 71.8932 43.3565 54.178 58.79 42.042C60.2796 40.8763 61.8457 39.8097 63.4766 38.8506C65.4522 33.0548 67.5013 28.6438 71.5615 23.8779C72.0962 23.2504 74.3011 20.9991 74.2256 20.4414ZM139.281 23.251C129.142 17.6987 115.248 13.8542 103.575 14.4121C93.8029 14.7108 85.0781 17.8517 78.2803 25.0664C72.4479 31.2563 69.4101 39.9258 69.8711 48.4365C70.8403 66.3252 83.0491 77.5651 95.0908 89.0303C107.759 101.091 123.12 112.161 131.084 127.973C132.37 130.527 133.36 133.295 134.063 136.042C134.342 137.13 134.907 140.565 135.214 141.232L135.443 141.211C136.144 140.23 136.784 138.093 137.213 136.929C139.832 129.826 140.709 122.355 139.626 114.873C137.489 100.106 126.116 89.6422 115.634 80.1016C111.304 76.1605 106.73 72.3274 102.438 68.335C94.7932 61.1825 85.858 52.6593 86.8213 41.2539C87.0576 38.1245 88.1357 35.1168 89.9414 32.5498C94.1714 26.5885 100.339 25.6157 107.08 25.9033C117.882 26.3641 127.788 30.4425 136.411 36.8213C141.241 40.405 145.6 44.5827 149.385 49.2549C161.767 65.3032 166.932 83.6642 164.329 103.867C161.837 123.339 151.791 141.054 136.357 153.19C134.868 154.356 133.303 155.423 131.672 156.382C129.696 162.177 127.647 166.588 123.587 171.354C123.053 171.981 120.847 174.232 120.923 174.79C121.534 175.131 121.916 174.999 122.604 174.805C143.582 168.824 161.278 154.663 171.709 135.505C182.275 116.505 184.904 94.1016 179.029 73.1709C173.054 51.8526 158.721 33.8511 139.281 23.251Z"
              fill="black"
            />
          </svg>
          <svg
            width="699"
            height="196"
            viewBox="0 0 699 196"
            fill="none"
            id="svg-1137231340_6119"
          >
            <path
              d="M668.903 75.4492C685.462 75.4492 698.806 91.3256 699 111.098C699 111.195 699 111.39 699 111.488C699 111.682 699 111.877 699 111.975C698.806 131.747 685.462 147.623 668.903 147.623C668.027 147.623 667.15 147.623 666.176 147.526L666.079 147.428C659.163 146.649 653.904 140.805 653.904 133.792V129.604H647.865V137.299V147.623V162.501L626.826 177.72V111.488V75.4492H647.865V85.7737V93.4684H653.904V89.2801C653.904 82.2673 659.163 76.3258 666.079 75.5466H666.176C667.15 75.4492 668.027 75.4492 668.903 75.4492ZM662.767 126.585C671.144 126.682 677.962 119.864 677.962 111.488C677.962 103.208 671.144 96.3904 662.767 96.4878C654.683 96.5852 647.962 103.208 647.865 111.39V111.488C647.865 111.585 647.865 111.585 647.865 111.682C647.962 119.864 654.683 126.487 662.767 126.585Z"
              fill="#030319"
            />
            <path
              d="M583.064 75.4492C602.933 75.4492 619.102 91.6177 619.102 111.488C619.102 131.455 602.933 147.623 583.064 147.623C563.096 147.623 546.928 131.455 546.928 111.488C546.928 91.6177 563.096 75.4492 583.064 75.4492ZM583.064 126.585C591.343 126.585 598.063 119.864 598.063 111.488C598.063 103.208 591.343 96.4878 583.064 96.4878C574.687 96.4878 567.966 103.208 567.966 111.488C567.966 119.864 574.687 126.585 583.064 126.585Z"
              fill="#030319"
            />
            <path
              d="M488.334 147.623V60.5712L509.373 45.3523V147.623H488.334ZM518.431 147.623V58.9032L539.47 43.6843V147.623H518.431Z"
              fill="#030319"
            />
            <path
              d="M459.569 85.7737V75.4492H480.608V111.488V147.623H459.569V137.299V129.604H453.53V133.792C453.53 140.805 448.368 146.649 441.355 147.428H441.258C440.381 147.526 439.407 147.623 438.531 147.623C422.07 147.623 408.628 131.747 408.434 111.975C408.434 111.78 408.434 111.682 408.434 111.488C408.434 111.39 408.434 111.195 408.434 111C408.628 91.3255 422.07 75.4492 438.531 75.4492C439.407 75.4492 440.381 75.4492 441.258 75.5466H441.355C448.368 76.3258 453.53 82.2673 453.53 89.2801V93.4684H459.569V85.7737ZM459.569 111.682C459.569 111.585 459.569 111.585 459.569 111.488V111.39C459.472 103.208 452.848 96.5852 444.667 96.4878C436.29 96.3904 429.472 103.208 429.472 111.488C429.472 119.864 436.29 126.585 444.667 126.585C452.848 126.487 459.472 119.767 459.569 111.682Z"
              fill="#030319"
            />
            <path
              d="M365.433 96.4878C357.056 96.4878 350.336 103.208 350.336 111.488C350.336 119.864 357.056 126.585 365.433 126.585C371.569 126.585 376.926 122.883 379.166 117.526H400.984C398.159 134.571 383.257 147.623 365.433 147.623C345.465 147.623 329.297 131.455 329.297 111.488C329.297 91.6177 345.465 75.4492 365.433 75.4492C383.257 75.4492 398.159 88.4035 400.984 105.546H379.166C376.926 100.189 371.569 96.4878 365.433 96.4878Z"
              fill="#030319"
            />
            <path
              d="M281.603 66.6326C266.839 66.6326 260.844 71.4881 260.844 77.8003C260.844 89.745 321.687 81.7819 321.687 115.479C321.687 120.335 315.245 147.623 278.739 147.623C254.492 147.623 244.023 133.833 239.37 127.715L238.744 126.938L254.724 115.283C254.724 115.283 262.365 126.647 278.739 126.647C293.592 126.647 299.587 121.792 299.587 115.479C299.587 103.243 238.744 111.692 238.744 77.8003C238.744 72.9448 245.186 45.6566 281.603 45.6566C305.94 45.6566 316.408 59.4464 321.061 65.5644L321.687 66.3412L305.707 77.6162C305.707 77.6162 298.066 66.6326 281.603 66.6326Z"
              fill="#030319"
            />
            <ellipse
              cx="97.8957"
              cy="97.8954"
              rx="97.8957"
              ry="97.8954"
              fill="url(#svg-1137231340_6119_paint0_linear_1843_2573)"
            />
            <path
              d="M103.576 14.4118C115.249 13.8539 129.142 17.6986 139.282 23.2509C158.722 33.8511 173.055 51.8528 179.03 73.1714C184.904 94.1022 182.275 116.505 171.709 135.505C161.278 154.663 143.583 168.825 122.604 174.805C121.916 175 121.535 175.131 120.923 174.79C120.848 174.232 123.053 171.981 123.588 171.354C127.648 166.588 129.697 162.178 131.673 156.382C133.304 155.423 134.869 154.356 136.358 153.19C151.792 141.054 161.837 123.339 164.329 103.867C166.932 83.664 161.768 65.3031 149.385 49.2548C145.6 44.5826 141.241 40.4056 136.412 36.8219C127.789 30.443 117.883 26.3644 107.081 25.9036C100.34 25.6159 94.1717 26.5885 89.9416 32.5503C88.1358 35.1174 87.0577 38.1249 86.8214 41.2544C85.8581 52.6598 94.7935 61.1826 102.438 68.3351C106.731 72.3277 111.304 76.1609 115.634 80.1021C126.117 89.6427 137.49 100.106 139.627 114.873C140.71 122.355 139.833 129.826 137.214 136.929C136.785 138.093 136.144 140.229 135.444 141.211L135.214 141.233C134.907 140.566 134.342 137.129 134.064 136.042C133.36 133.295 132.371 130.526 131.084 127.972C123.12 112.161 107.759 101.092 95.0915 89.0304C83.0497 77.5651 70.841 66.3252 69.8719 48.4363C69.4109 39.9256 72.4487 31.2566 78.2811 25.0667C85.079 17.8519 93.8037 14.7104 103.576 14.4118Z"
              fill="white"
            />
            <path
              d="M91.5742 180.82C79.901 181.378 66.0082 177.534 55.8688 171.981C36.4285 161.381 22.0955 143.379 16.1206 122.061C10.246 101.13 12.8756 78.7274 23.4413 59.7273C33.8727 40.5694 51.5673 26.4075 72.5462 20.4273C73.234 20.2325 73.6158 20.1012 74.2273 20.442C74.3028 20.9998 72.097 23.2514 71.5628 23.8785C67.5026 28.6444 65.4533 33.0547 63.4777 38.8504C61.8468 39.8095 60.2815 40.8763 58.7919 42.042C43.3582 54.178 33.3134 71.8929 30.8211 91.3651C28.218 111.568 33.3828 129.929 45.765 145.977C49.5501 150.65 53.9092 154.827 58.7385 158.41C67.3615 164.789 77.2674 168.868 88.0694 169.329C94.8106 169.616 100.979 168.644 105.209 162.682C107.015 160.115 108.093 157.107 108.329 153.978C109.292 142.572 100.357 134.05 92.7125 126.897C88.4197 122.904 83.8462 119.071 79.5159 115.13C69.0339 105.589 57.6601 95.1262 55.5235 80.3593C54.4404 72.877 55.3169 65.4066 57.9365 58.3035C58.3658 57.1393 59.0062 55.0028 59.7064 54.0216L59.9363 53.9994C60.2437 54.6664 60.8081 58.1028 61.0866 59.1903C61.7902 61.9377 62.7798 64.7057 64.0662 67.2598C72.0303 83.0713 87.391 94.1406 100.059 106.202C112.101 117.667 124.309 128.907 125.278 146.796C125.739 155.307 122.702 163.976 116.869 170.165C110.071 177.38 101.347 180.522 91.5742 180.82Z"
              fill="white"
            />
            <defs>
              <linearGradient
                id="svg-1137231340_6119_paint0_linear_1843_2573"
                x1="97.8957"
                y1="0"
                x2="46.0375"
                y2="179.202"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0F1AF1" />
                <stop offset="1" stopColor="#2E38F9" />
              </linearGradient>
            </defs>
          </svg>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            id="svg-1839680863_948"
          >
            <path
              d="M9.90033 7.61699C10.967 7.66699 11.242 8.57533 11.2587 9.00033H12.7503C12.6837 7.35033 11.5087 6.34199 9.87533 6.34199C8.03366 6.34199 6.66699 7.50033 6.66699 10.117C6.66699 11.7337 7.44199 13.6503 9.86699 13.6503C11.717 13.6503 12.7087 12.2753 12.7337 11.192H11.242C11.217 11.6837 10.867 12.342 9.88366 12.392C8.79199 12.3587 8.33366 11.5087 8.33366 10.117C8.33366 7.70866 9.40033 7.63366 9.90033 7.61699ZM10.0003 1.66699C5.40033 1.66699 1.66699 5.40033 1.66699 10.0003C1.66699 14.6003 5.40033 18.3337 10.0003 18.3337C14.6003 18.3337 18.3337 14.6003 18.3337 10.0003C18.3337 5.40033 14.6003 1.66699 10.0003 1.66699ZM10.0003 16.667C6.32533 16.667 3.33366 13.6753 3.33366 10.0003C3.33366 6.32533 6.32533 3.33366 10.0003 3.33366C13.6753 3.33366 16.667 6.32533 16.667 10.0003C16.667 13.6753 13.6753 16.667 10.0003 16.667Z"
              fill="#666D80"
            />
          </svg>
        </>
      </div>
      <>
        {/*$*/}
        <div
          data-framer-root=""
          className="framer-prcRG framer-rcOGl framer-tx7Wz framer-123arm cards-s1"
        >
          <div
            className="framer-1elm4va-container hidden-1u67xp5 cards-s2"
            data-framer-appear-id="1elm4va"
            id="1elm4va"
          >
            <div className="ssr-variant hidden-do98mh">
              <div
                className="framer-8MFL4 framer-15zawlh framer-v-15zawlh cards-s3"
                data-framer-name="Desktop [Default Boxed]"
              >
                <header
                  className="framer-ooxjtq cards-s4"
                  data-border="true"
                  data-framer-name="Main Area"
                >
                  <div
                    className="framer-p578i7 cards-s5"
                    data-framer-name="Background [Default]"
                  />
                  <div
                    className="framer-pc27k4 cards-s6"
                    data-framer-name="Background [Boxed Mini]"
                  />
                  <div className="framer-cjwofg" data-framer-name="Content">
                    {/*$*/}
                    <a className="framer-1jy87r1 framer-145ct8v" href="/">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1hjthz cards-s7"
                        aria-hidden="true"
                      />
                    </a>
                    {/*/$*/}
                    <nav className="framer-xe9vp0" data-framer-name="Nav">
                      <div
                        className="framer-1hg3ikx-container"
                        data-framer-name="Menu [Desktop]"
                        name="Menu [Desktop]"
                      >
                        <div
                          name="Menu [Desktop]"
                          className="framer-n0D3w framer-1itjusz framer-v-1itjusz cards-s8"
                          data-framer-name="Menu"
                        >
                          {/*$*/}
                          <div className="framer-mquu2a-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/accounts"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Accounts{' '}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1yixouk-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/scallop-pay"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Scallop Pay
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-wi7xty-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/cards"
                              data-framer-page-link-current="true"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Cards
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1tpzm7c-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/scallop-plus"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Scallop+
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-u2qi4r-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/helpdesk"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  FAQs
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                    </nav>
                    <div
                      className="framer-14uxsxw"
                      data-framer-name="Menu Icon"
                    >
                      <div
                        className="framer-16051d2"
                        data-framer-name="QR Overlay Trigger"
                        data-highlight="true"
                        tabIndex={0}
                      >
                        <div className="framer-168591e-container">
                          <div
                            className="framer-wWHmH framer-ly6l8a framer-v-18roq29 cards-s12"
                            data-border="true"
                            data-framer-name="Variant 2"
                            data-highlight="true"
                            tabIndex={0}
                          >
                            <div
                              className="framer-1ebu3nm cards-s13 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s14">
                                Get Started
                              </p>
                            </div>
                            <div
                              className="framer-1u4w7ze cards-s15 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p className="framer-text cards-s16">
                                Coming soon
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </header>
              </div>
            </div>
            <div className="ssr-variant hidden-123arm hidden-1u67xp5">
              <div
                className="framer-8MFL4 framer-15zawlh framer-v-15viyb0 cards-s3"
                data-framer-name="Desktop [Boxed Mini]"
              >
                <header
                  className="framer-ooxjtq cards-s17"
                  data-border="true"
                  data-framer-name="Main Area"
                >
                  <div
                    className="framer-p578i7 cards-s6"
                    data-framer-name="Background [Default]"
                  />
                  <div
                    className="framer-pc27k4 cards-s5"
                    data-framer-name="Background [Boxed Mini]"
                  />
                  <div className="framer-cjwofg" data-framer-name="Content">
                    {/*$*/}
                    <a className="framer-1jy87r1 framer-145ct8v" href="/">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1hjthz cards-s7"
                        aria-hidden="true"
                      />
                    </a>
                    {/*/$*/}
                    <nav className="framer-xe9vp0" data-framer-name="Nav">
                      <div
                        className="framer-1hg3ikx-container"
                        data-framer-name="Menu [Desktop]"
                        name="Menu [Desktop]"
                      >
                        <div
                          name="Menu [Desktop]"
                          className="framer-n0D3w framer-1itjusz framer-v-1itjusz cards-s8"
                          data-framer-name="Menu"
                        >
                          {/*$*/}
                          <div className="framer-mquu2a-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/accounts"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Accounts{' '}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1yixouk-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/scallop-pay"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Scallop Pay
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-wi7xty-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/cards"
                              data-framer-page-link-current="true"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Cards
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1tpzm7c-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/scallop-plus"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Scallop+
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-u2qi4r-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/helpdesk"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  FAQs
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                    </nav>
                    <div
                      className="framer-14uxsxw"
                      data-framer-name="Menu Icon"
                    >
                      <div
                        className="framer-16051d2"
                        data-framer-name="QR Overlay Trigger"
                        data-highlight="true"
                        tabIndex={0}
                      >
                        <div className="framer-168591e-container">
                          <div
                            className="framer-wWHmH framer-ly6l8a framer-v-18roq29 cards-s12"
                            data-border="true"
                            data-framer-name="Variant 2"
                            data-highlight="true"
                            tabIndex={0}
                          >
                            <div
                              className="framer-1ebu3nm cards-s13 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s14">
                                Get Started
                              </p>
                            </div>
                            <div
                              className="framer-1u4w7ze cards-s15 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p className="framer-text cards-s16">
                                Coming soon
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </header>
              </div>
            </div>
          </div>
          <div className="ssr-variant hidden-1u67xp5">
            <div
              className="framer-pazhod-container hidden-123arm hidden-do98mh cards-s2"
              data-framer-appear-id="pazhod"
            >
              <div
                className="framer-8MFL4 framer-15zawlh framer-v-15zawlh cards-s3"
                data-framer-name="Desktop [Default Boxed]"
              >
                <header
                  className="framer-ooxjtq cards-s4"
                  data-border="true"
                  data-framer-name="Main Area"
                >
                  <div
                    className="framer-p578i7 cards-s5"
                    data-framer-name="Background [Default]"
                  />
                  <div
                    className="framer-pc27k4 cards-s6"
                    data-framer-name="Background [Boxed Mini]"
                  />
                  <div className="framer-cjwofg" data-framer-name="Content">
                    {/*$*/}
                    <a className="framer-1jy87r1 framer-145ct8v" href="/">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1hjthz cards-s7"
                        aria-hidden="true"
                      />
                    </a>
                    {/*/$*/}
                    <nav className="framer-xe9vp0" data-framer-name="Nav">
                      <div
                        className="framer-1hg3ikx-container"
                        data-framer-name="Menu [Desktop]"
                        name="Menu [Desktop]"
                      >
                        <div
                          name="Menu [Desktop]"
                          className="framer-n0D3w framer-1itjusz framer-v-1itjusz cards-s8"
                          data-framer-name="Menu"
                        >
                          {/*$*/}
                          <div className="framer-mquu2a-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/accounts"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Accounts{' '}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1yixouk-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/scallop-pay"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Scallop Pay
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-wi7xty-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/cards"
                              data-framer-page-link-current="true"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Cards
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1tpzm7c-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/scallop-plus"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  Scallop+
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-u2qi4r-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq cards-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/helpdesk"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi cards-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s11">
                                  FAQs
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                    </nav>
                    <div
                      className="framer-14uxsxw"
                      data-framer-name="Menu Icon"
                    >
                      <div
                        className="framer-16051d2"
                        data-framer-name="QR Overlay Trigger"
                        data-highlight="true"
                        tabIndex={0}
                      >
                        <div className="framer-168591e-container">
                          <div
                            className="framer-wWHmH framer-ly6l8a framer-v-18roq29 cards-s12"
                            data-border="true"
                            data-framer-name="Variant 2"
                            data-highlight="true"
                            tabIndex={0}
                          >
                            <div
                              className="framer-1ebu3nm cards-s13 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s14">
                                Get Started
                              </p>
                            </div>
                            <div
                              className="framer-1u4w7ze cards-s15 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p className="framer-text cards-s16">
                                Coming soon
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </header>
              </div>
            </div>
          </div>
          <div className="ssr-variant hidden-123arm hidden-do98mh">
            <div
              className="framer-pazhod-container hidden-123arm hidden-do98mh"
              data-framer-appear-id="pazhod"
            >
              <div
                className="framer-8MFL4 framer-15zawlh framer-v-ewbo4l cards-s3"
                data-framer-name="Mobile"
              >
                <header
                  className="framer-ooxjtq cards-s18"
                  data-border="true"
                  data-framer-name="Main Area"
                >
                  <div
                    className="framer-p578i7 cards-s5"
                    data-framer-name="Background [Default]"
                  />
                  <div
                    className="framer-pc27k4 cards-s6"
                    data-framer-name="Background [Boxed Mini]"
                  />
                  <div className="framer-cjwofg" data-framer-name="Content">
                    {/*$*/}
                    <a className="framer-1jy87r1 framer-145ct8v" href="/">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1hjthz cards-s7"
                        aria-hidden="true"
                      />
                    </a>
                    {/*/$*/}
                    <nav className="framer-xe9vp0" data-framer-name="Nav" />
                    <div
                      className="framer-14uxsxw"
                      data-framer-name="Menu Icon"
                    >
                      <div className="framer-1yr51k6-container">
                        {/*$*/}
                        <div
                          className="framer-OuhX3 framer-1ib4qbi framer-v-1ib4qbi cards-s19"
                          data-framer-name="Mobile Menu"
                          data-highlight="true"
                          tabIndex={0}
                        >
                          <div className="framer-1cux5of cards-s20" />
                          <div className="framer-1w1bul6 cards-s20" />
                          <div className="framer-1ku2ucf cards-s21" />
                        </div>
                        {/*/$*/}
                      </div>
                    </div>
                  </div>
                </header>
              </div>
            </div>
          </div>
          <div className="framer-1gvz2ot" data-framer-name="container">
            <div className="framer-e7nm6p" data-framer-name="hero" id="main-1">
              <div className="framer-1j6blrt" data-framer-name="Sub Container">
                <div className="framer-oytc8e">
                  <div
                    className="framer-lpzndg reveal"
                    data-framer-appear-id="lpzndg"
                    data-framer-name="Heading"
                    data-framer-component-type="RichTextContainer"
                  >
                    <h1
                      className="framer-text framer-styles-preset-18sajzf cards-s22"
                      data-styles-preset="nXmNeSqma"
                      dir="auto"
                    >
                      The only card you&apos;ll ever need
                    </h1>
                  </div>
                </div>
                <div
                  className="framer-76g0ch"
                  data-framer-name="Text Container"
                >
                  <div className="ssr-variant hidden-1u67xp5">
                    <div
                      className="framer-1liw33u reveal"
                      data-framer-appear-id="1liw33u"
                      data-framer-name="Description"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text cards-s23">
                        Use your balance to pay anywhere. Fund your card with
                        fiat, stablecoins, or digital assets in one click.
                      </p>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-123arm hidden-do98mh">
                    <div
                      className="framer-1liw33u reveal"
                      data-framer-appear-id="1liw33u"
                      data-framer-name="Description"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text cards-s24">
                        Use your balance to pay anywhere. Fund your card with
                        fiat, stablecoins, or digital assets in one click.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="ssr-variant hidden-1u67xp5">
                  {/*$*/}
                  <a
                    className="framer-188ex54 framer-17joz64"
                    data-framer-name="Button"
                    id="188ex54"
                    href="https://onelink.to/wch597"
                    target="_blank"
                    rel="noopener"
                    tabIndex={0}
                  >
                    <div
                      className="framer-1jmm20o cards-s25 reveal"
                      data-framer-name="Label"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text cards-s26">
                        Start spending globally
                      </p>
                    </div>
                  </a>
                  {/*/$*/}
                </div>
                <div className="ssr-variant hidden-123arm hidden-do98mh">
                  {/*$*/}
                  <a
                    className="framer-188ex54 framer-17joz64"
                    data-framer-name="Button"
                    id="188ex54"
                    href="https://onelink.to/wch597"
                    target="_blank"
                    rel="noopener"
                    tabIndex={0}
                  >
                    <div
                      className="framer-1jmm20o cards-s25 reveal"
                      data-framer-name="Label"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text cards-s26">
                        Start spending globally
                      </p>
                    </div>
                  </a>
                  {/*/$*/}
                </div>
              </div>
              <div className="framer-h88bsc">
                <div className="ssr-variant hidden-do98mh hidden-1u67xp5">
                  <div className="framer-erdh9d cards-s27">
                    <div
                      data-framer-background-image-wrapper="true"
                      className="cards-s28"
                    >
                      <img
                        decoding="async"
                        width="3344"
                        height="1882"
                        sizes="(min-width: 1440px) calc(100vw * 1.0028), (min-width: 810px) and (max-width: 1439.98px) 1222.4612px, (max-width: 809.98px) 612px"
                        srcSet="/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 512w,/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 1024w,/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 2048w,/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 3344w"
                        src="/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png"
                        alt=""
                        className="cards-s29"
                      />
                    </div>
                  </div>
                </div>
                <div className="ssr-variant hidden-123arm hidden-1u67xp5">
                  <div className="framer-erdh9d cards-s30">
                    <div
                      data-framer-background-image-wrapper="true"
                      className="cards-s28"
                    >
                      <img
                        decoding="async"
                        width="3344"
                        height="1882"
                        sizes="(min-width: 1440px) calc(100vw * 1.0028), (min-width: 810px) and (max-width: 1439.98px) 1222.4612px, (max-width: 809.98px) 612px"
                        srcSet="/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 512w,/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 1024w,/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 2048w,/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 3344w"
                        src="/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png"
                        alt=""
                        className="cards-s29"
                      />
                    </div>
                  </div>
                </div>
                <div className="ssr-variant hidden-123arm hidden-do98mh">
                  <div className="framer-erdh9d">
                    <div
                      data-framer-background-image-wrapper="true"
                      className="cards-s28"
                    >
                      <img
                        decoding="async"
                        width="3344"
                        height="1882"
                        sizes="(min-width: 1440px) calc(100vw * 1.0028), (min-width: 810px) and (max-width: 1439.98px) 1222.4612px, (max-width: 809.98px) 612px"
                        srcSet="/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 512w,/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 1024w,/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 2048w,/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png 3344w"
                        src="/framerusercontent.com/images/b3zLplU2jsmqcL6CptZgi8kxXo.png"
                        alt=""
                        className="cards-s29"
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="framer-wck6ha hidden-1u67xp5"
                  data-framer-name="Gradient"
                />
                <div
                  className="framer-lhk2zx hidden-1u67xp5 cards-s31"
                  data-framer-name="Gradient"
                />
              </div>
            </div>
            <div className="framer-1amcq08" data-framer-name="card balalce">
              <div className="framer-18q4ssu" data-framer-name="Section">
                <div className="framer-zruakm" data-framer-name="Content">
                  <div className="framer-10scx5y" data-framer-name="title">
                    <div
                      className="framer-1wm51ia"
                      data-border="true"
                      data-framer-name="Frame 40889"
                    >
                      <div
                        className="framer-14q2use cards-s25 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p className="framer-text cards-s32">Cards</p>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-do98mh hidden-1u67xp5">
                      <div
                        className="framer-1a3c0mv reveal"
                        data-framer-name="Heading"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h2
                          className="framer-text framer-styles-preset-120m1vw cards-s22"
                          data-styles-preset="Sp_YUSQNX"
                          dir="auto"
                        >
                          Your balance. Your
                          <br className="framer-text" />
                          choice.
                        </h2>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-123arm hidden-1u67xp5">
                      <div
                        className="framer-1a3c0mv reveal"
                        data-framer-name="Heading"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h2
                          className="framer-text framer-styles-preset-120m1vw cards-s22"
                          data-styles-preset="Sp_YUSQNX"
                          dir="auto"
                        >
                          Built for how money actually moves
                        </h2>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-123arm hidden-do98mh">
                      <div
                        className="framer-1a3c0mv cards-s33 reveal"
                        data-framer-name="Heading"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h1 dir="auto" className="framer-text cards-s34">
                          Your balance. Your
                          <br className="framer-text" />
                          choice.
                        </h1>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-1u67xp5">
                      <div
                        className="framer-8ohkmm reveal"
                        data-framer-name="Description"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s35">
                          Use any asset you hold to fund your card in seconds.
                        </p>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-123arm hidden-do98mh">
                      <div
                        className="framer-8ohkmm reveal"
                        data-framer-name="Description"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s36">
                          Use any asset you hold to fund your card in seconds.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="framer-1fe9faq" data-framer-name="Container">
                    <div
                      className="framer-1lpk34d"
                      data-framer-name="Container"
                    >
                      <div
                        className="framer-18fu8pc"
                        data-framer-name="Container"
                      >
                        <div
                          className="framer-1hurnz5"
                          data-framer-name="Container"
                        >
                          <div
                            className="framer-16obv59"
                            data-framer-name="Heading + Badge"
                          >
                            <div
                              className="framer-8x1zzu cards-s37 reveal"
                              data-framer-name="Automate Meeting Scheduling"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h3 dir="auto" className="framer-text cards-s38">
                                Top up from fiat, stablecoins, or digital assets
                              </h3>
                            </div>
                            <div
                              className="framer-cdiam7 reveal reveal-1"
                              data-framer-appear-id="cdiam7"
                              data-framer-name="Our solution reduces manual effort, minimizes errors, and ensures seamless coordination, allowing you to focus on what truly matters."
                              data-framer-component-type="RichTextContainer"
                            >
                              <ul dir="auto" className="framer-text cards-s39">
                                <li data-preset-tag="p" className="framer-text">
                                  <p className="framer-text">
                                    Switch between balances anytime
                                  </p>
                                </li>
                                <li data-preset-tag="p" className="framer-text">
                                  <p className="framer-text">
                                    Instant conversion during top-up
                                  </p>
                                </li>
                                <li data-preset-tag="p" className="framer-text">
                                  <p className="framer-text">
                                    Everything happens in one app
                                  </p>
                                </li>
                                <li data-preset-tag="p" className="framer-text">
                                  <p className="framer-text">
                                    Auto top-up available
                                  </p>
                                </li>
                              </ul>
                            </div>
                            <div
                              className="framer-pv8jix-container cards-s40"
                              data-framer-appear-id="pv8jix"
                            >
                              {/*$*/}
                              <section className="cards-s41">
                                <div className="cards-marquee-track">
                                  <ul className="cards-s42">{cardTagItems}</ul>
                                  <ul className="cards-s42" aria-hidden="true">
                                    {cardTagItems}
                                  </ul>
                                </div>
                              </section>
                              {/*/$*/}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-4iaetk"
                        data-framer-name="Container"
                      >
                        <div className="framer-1291c46-container">
                          {/*$*/}
                          <div className="ssr-variant hidden-1u67xp5">
                            <div className="cards-s19" />
                          </div>
                          <div className="ssr-variant hidden-123arm hidden-do98mh">
                            <div className="cards-s3" />
                          </div>
                          {/*/$*/}
                        </div>
                        <div
                          className="framer-1m8v5os"
                          data-framer-name="Gradient"
                        />
                        <div
                          className="framer-rjxcro"
                          data-framer-name="Gradient"
                        />
                        <div className="phone-orbit-wrapper">
                          <div className="phone-orbit-viewport">
                            <div className="phone-orbit">
                              {[
                                { src: '/framerusercontent.com/img1.png', slot: -3 },
                                { src: '/framerusercontent.com/img2.png', slot: -2 },
                                { src: '/framerusercontent.com/img3.png', slot: -1 },
                                { src: '/framerusercontent.com/img1.png', slot: 0 },
                                { src: '/framerusercontent.com/img2.png', slot: 1 },
                                { src: '/framerusercontent.com/img3.png', slot: 2 },
                              ].map(({ src, slot }, i) => (
                                <div
                                  key={i}
                                  className="phone-orbit-item"
                                  style={phoneStyle(slot)}
                                >
                                  <img src={src} alt="" className="phone-orbit-img" />
                                </div>
                              ))}
                            </div>
                            <div className="cards-orbit-fade cards-orbit-fade-left" />
                            <div className="cards-orbit-fade cards-orbit-fade-right" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="framer-1kj40sk"
                      data-framer-name="Container"
                    >
                      <div
                        className="framer-2010j0"
                        data-framer-name="Container"
                      >
                        <div className="framer-tpuau0-container">
                          {/*$*/}
                          <div className="scallop-tx-animation" aria-label="Animated transactions">
                            <div className="scallop-tx-glow" />
                            <div className="scallop-tx-viewport">
                              <div
                                className="scallop-tx-track"
                                style={{
                                  transform: `translateY(calc(-1 * var(--card-step) * ${txStep}))`,
                                  transitionDuration: txInstant ? '0ms' : `${TX_TRANSITION_MS}ms`,
                                }}
                              >
                                {txTrackItems.map((item, index) => (
                                  <div className="scallop-tx-slot" key={`${item.id}-${index}`}>
                                    <img
                                      src={item.img}
                                      alt=""
                                      className={
                                        item.id === 'swap'
                                          ? 'scallop-tx-card-img scallop-tx-card-img-swap'
                                          : 'scallop-tx-card-img'
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                      <div
                        className="framer-obl0oo"
                        data-framer-name="Container"
                      >
                        <div
                          className="framer-1fl0ovk"
                          data-framer-name="Container"
                        >
                          <div
                            className="framer-bt6y8f cards-s25 reveal"
                            data-framer-name="Seamless Integration"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p dir="auto" className="framer-text cards-s56">
                              Your card activity, in real time
                            </p>
                          </div>
                          <div
                            className="framer-1j0jc4j reveal reveal-1"
                            data-framer-appear-id="1j0jc4j"
                            data-framer-name="Streamline operations effortlessly with our integrated tool solutions, ensuring a cohesive workflow environment that boosts efficiency across your organization. Simplify operations and enhance efficiency through unified tool integration."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p dir="auto" className="framer-text cards-s57">
                              Track payments, top-ups, and conversions across
                              fiat and digital assets, all in one place.
                            </p>
                          </div>
                          <div className="ssr-variant hidden-1u67xp5">
                            {/*$*/}
                            <a
                              className="framer-1i2ohmi framer-17joz64"
                              data-framer-name="Button"
                              id="1i2ohmi"
                              href="https://onelink.to/wch597"
                              target="_blank"
                              rel="noopener"
                              tabIndex={0}
                            >
                              <div
                                className="framer-1vysl4d cards-s25 reveal"
                                data-framer-name="Label"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s26">
                                  Start using your card{' '}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          <div className="ssr-variant hidden-123arm hidden-do98mh">
                            {/*$*/}
                            <a
                              className="framer-1i2ohmi framer-17joz64"
                              data-framer-name="Button"
                              id="1i2ohmi"
                              href="https://onelink.to/wch597"
                              target="_blank"
                              rel="noopener"
                              tabIndex={0}
                            >
                              <div
                                className="framer-1vysl4d cards-s25 reveal"
                                data-framer-name="Label"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p dir="auto" className="framer-text cards-s26">
                                  Start using your card{' '}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="framer-1bb5wfj"
              data-framer-name="secure and protected"
              id="trigger-lock"
            >
              <div className="framer-ox4471" data-framer-name="Hero text">
                <div
                  className="framer-19hgkm6"
                  data-framer-name="Group 48095875"
                >
                  <div
                    className="framer-1s5ypoz"
                    data-framer-name="Frame 1400001633"
                  >
                    <div
                      data-framer-component-type="SVG"
                      data-framer-name="graphic"
                      parentsize="0"
                      _constraints="[object Object]"
                      rotation="0"
                      shadows=""
                      className="framer-1jx3nz9 cards-s58"
                      aria-hidden="true"
                    >
                      <div className="svgContainer cards-s59">
                        <svg viewBox="0 0 48 48" className="cards-s60">
                          <use href="#svg-539090904_324" />
                        </svg>
                      </div>
                    </div>
                    <div className="framer-8s450n">
                      <div className="framer-1401omj">
                        <div
                          className="framer-1ntzjnd"
                          data-border="true"
                          data-framer-name="intro"
                        >
                          <div
                            className="framer-lgay0x cards-s25 reveal"
                            data-framer-name="New role, who dis?"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h6 className="framer-text cards-s61">
                              Peace of mind
                            </h6>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="framer-10j105t">
                      <div className="ssr-variant hidden-1u67xp5">
                        <div
                          className="framer-106nug2 cards-s25 reveal"
                          data-framer-name="Elevate your community to a powerful revenue hub"
                          data-framer-component-type="RichTextContainer"
                        >
                          <h1 className="framer-text cards-s62">
                            <span
                              data-text-fill="true"
                              className="framer-text cards-s63"
                            >
                              SECURE CARDS
                            </span>
                          </h1>
                        </div>
                      </div>
                      <div className="ssr-variant hidden-123arm hidden-do98mh">
                        <div
                          className="framer-106nug2 cards-s25 reveal"
                          data-framer-name="Elevate your community to a powerful revenue hub"
                          data-framer-component-type="RichTextContainer"
                        >
                          <h1 className="framer-text cards-s64">
                            <span
                              data-text-fill="true"
                              className="framer-text cards-s63"
                            >
                              SECURE CARDS
                            </span>
                          </h1>
                        </div>
                      </div>
                      <div className="ssr-variant hidden-1u67xp5">
                        <div
                          className="framer-1je2m4u cards-s25 reveal"
                          data-framer-name="Elevate your community to a powerful revenue hub"
                          data-framer-component-type="RichTextContainer"
                        >
                          <h1 className="framer-text cards-s62">
                            ALWAYS PROTECTED
                          </h1>
                        </div>
                      </div>
                      <div className="ssr-variant hidden-123arm hidden-do98mh">
                        <div
                          className="framer-1je2m4u cards-s25 reveal"
                          data-framer-name="Elevate your community to a powerful revenue hub"
                          data-framer-component-type="RichTextContainer"
                        >
                          <h1 className="framer-text cards-s64">
                            ALWAYS PROTECTED
                          </h1>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="framer-fk5uw5 cards-s25 reveal"
                    data-framer-name="Build rich user connections, spark action with tailored CTAs,\u2028and tap into new revenue channels - all within Discord!"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p className="framer-text cards-s65">
                      We recognize the significance of safeguarding and
                      confidentiality, especially when managing essential user
                      information. Here&apos;s how we emphasize its importance:
                    </p>
                  </div>
                </div>
              </div>
              <div className="framer-19c87ng" data-framer-name="Container">
                <div
                  className="framer-ooypq0"
                  data-border="true"
                  data-framer-name="Step"
                >
                  <figure className="framer-1gj7n81" data-framer-name="Images">
                    <div
                      className="framer-q0lrhn cards-s66"
                      data-framer-name="Image"
                    >
                      <div
                        data-framer-background-image-wrapper="true"
                        className="cards-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="499"
                          height="1024"
                          sizes="(min-width: 1440px) 184px, (min-width: 810px) and (max-width: 1439.98px) 184px, (max-width: 809.98px) 184px"
                          srcSet="/framerusercontent.com/images/ziyTOlnaFSkF7ev3Uu3touDzt48.png 499w"
                          src="/framerusercontent.com/images/ziyTOlnaFSkF7ev3Uu3touDzt48.png"
                          alt=""
                          className="cards-s29"
                        />
                      </div>
                    </div>
                    <figure
                      className="framer-1oilzhx"
                      data-framer-name="Black Gradient"
                    />
                  </figure>
                  <div className="framer-vxaiu2" data-framer-name="Content">
                    <div className="ssr-variant hidden-do98mh">
                      <div
                        className="framer-88ds7n cards-s37 reveal"
                        data-framer-name="Setup Your Account"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h6 dir="auto" className="framer-text cards-s67">
                          Smile please, your Scallop cards are protected with
                          top-level security.
                        </h6>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-123arm hidden-1u67xp5">
                      <div
                        className="framer-88ds7n cards-s37 reveal"
                        data-framer-name="Setup Your Account"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h6 dir="auto" className="framer-text cards-s68">
                          Smile please, your Scallop cards are protected with
                          top-level security.
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="framer-exhpei"
                  data-border="true"
                  data-framer-name="Step"
                >
                  <figure className="framer-1rkt1bm" data-framer-name="Images">
                    <div
                      className="framer-1y7e4by cards-s66"
                      data-framer-name="Image"
                    >
                      <div
                        data-framer-background-image-wrapper="true"
                        className="cards-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="499"
                          height="1024"
                          sizes="(min-width: 1440px) 184px, (min-width: 810px) and (max-width: 1439.98px) 184px, (max-width: 809.98px) 184px"
                          srcSet="/framerusercontent.com/images/RIuWV0Pc5qBaScvB2srlMDJe9I.png 499w"
                          src="/framerusercontent.com/images/RIuWV0Pc5qBaScvB2srlMDJe9I.png"
                          alt=""
                          className="cards-s29"
                        />
                      </div>
                    </div>
                    <figure
                      className="framer-1h5v0qy"
                      data-framer-name="Black Gradient"
                    />
                  </figure>
                  <div className="framer-7inyi1" data-framer-name="Content">
                    <div className="ssr-variant hidden-do98mh">
                      <div
                        className="framer-m6acyf cards-s37 reveal"
                        data-framer-name="Setup Your Account"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h6 dir="auto" className="framer-text cards-s67">
                          If in doubt, freeze - and unfreeze - your card in
                          seconds
                        </h6>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-123arm hidden-1u67xp5">
                      <div
                        className="framer-m6acyf cards-s37 reveal"
                        data-framer-name="Setup Your Account"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h6 dir="auto" className="framer-text cards-s68">
                          If in doubt, freeze - and unfreeze - your card in
                          seconds
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="framer-1qab0ju"
                  data-border="true"
                  data-framer-name="Step"
                >
                  <figure className="framer-ibz2uq" data-framer-name="Images">
                    <div
                      className="framer-1813r0i cards-s66"
                      data-framer-name="Image"
                    >
                      <div
                        data-framer-background-image-wrapper="true"
                        className="cards-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="499"
                          height="1024"
                          sizes="(min-width: 1440px) 184px, (min-width: 810px) and (max-width: 1439.98px) 184px, (max-width: 809.98px) 184px"
                          srcSet="/framerusercontent.com/images/PQcTjuXxsXak9dvoiNZULWBhnOA.png 499w"
                          src="/framerusercontent.com/images/PQcTjuXxsXak9dvoiNZULWBhnOA.png"
                          alt=""
                          className="cards-s29"
                        />
                      </div>
                    </div>
                  </figure>
                  <div className="framer-14nba1g" data-framer-name="Content">
                    <div className="ssr-variant hidden-do98mh">
                      <div
                        className="framer-15reroa cards-s37 reveal"
                        data-framer-name="Setup Your Account"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h6 dir="auto" className="framer-text cards-s67">
                          Set your spending, withdrawal, and contactless
                          spending limits.
                        </h6>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-123arm hidden-1u67xp5">
                      <div
                        className="framer-15reroa cards-s37 reveal"
                        data-framer-name="Setup Your Account"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h6 dir="auto" className="framer-text cards-s68">
                          Customize Your Card with Your Own Unique
                          <br className="framer-text" />
                          Touch
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="framer-dfhl9r"
                data-border="true"
                data-framer-name="Background video"
              >
                <div className="framer-6iqwpd-container">
                  {/*$*/}
                  <video
                    src="/framerusercontent.com/assets/sEstir6QqlYbreEtAwsqx1c27KU.mp4"
                    loop
                    muted
                    playsInline
                    autoPlay
                    className="cards-s69"
                  />
                  {/*/$*/}
                </div>
                <div
                  className="framer-1j7iaos"
                  data-framer-name="desaturation"
                />
                <div className="framer-ngfdoa" data-framer-name="cover" />
                <div
                  className="framer-15agxry"
                  data-framer-name="CHANGE THIS - accent color"
                />
              </div>
            </div>
            <div className="ssr-variant hidden-1u67xp5">
              <div
                className="framer-1bge27b hidden-do98mh cards-s33"
                data-framer-name="built in security"
              >
                <div
                  className="framer-xzdbie hidden-123arm cards-s25 reveal"
                  data-framer-name="Track your spendings"
                  data-framer-component-type="RichTextContainer"
                >
                  <h3 dir="auto" className="framer-text cards-s70">
                    Full control. Built-in security.
                  </h3>
                </div>
                <div className="framer-a2vdbr" data-framer-name="Text Wrapper">
                  <div className="framer-5tcjk9">
                    <div className="framer-19vwvj">
                      <div
                        className="framer-avhx5q hidden-1u67xp5 reveal"
                        data-framer-name="Track your spendings"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h3 dir="auto" className="framer-text cards-s70">
                          Full control. Built-in security.
                        </h3>
                      </div>
                      <div
                        className="framer-1ldknob reveal"
                        data-framer-name="Automatically categorize your spending, track where your money goes, and gain insights into your habits."
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s71">
                          Manage your card in real time with simple controls
                          designed to keep your money safe.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="framer-h7e29a">
                    <div className="framer-m3amiq-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Freeze or unfreeze your card instantly
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-2h2p0h-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Track every transaction as it happens
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-15atbpa-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Monitor usage across all your balances
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-1ulc994-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Stay protected with built-in security at every step
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="framer-dnuzij"
                  data-border="true"
                  data-framer-name="Mockup 2"
                >
                  <div className="framer-17ebt7m" data-framer-name="Image">
                    <div className="framer-16p6any">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="cards-s28"
                      >
                        <img
                          decoding="async"
                          width="499"
                          height="1024"
                          sizes="(min-width: 1440px) max(269.1644px, 100vw), (min-width: 810px) and (max-width: 1439.98px) max(269.1644px, max((max((100vw * 0.9975 - 80px) / 2.25, 1px) - 48px) * 0.7, 1px)), (max-width: 809.98px) max(max((100vw - 80px) * 0.7, 1px), 100vw)"
                          srcSet="/framerusercontent.com/images/XavmE8bUfvIT5ZIrBMvGdaz9s.png 499w"
                          src="/framerusercontent.com/images/XavmE8bUfvIT5ZIrBMvGdaz9s.png"
                          alt=""
                          className="cards-s75"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="ssr-variant hidden-123arm hidden-do98mh">
              <div
                className="framer-1bge27b hidden-do98mh"
                data-framer-name="built in security"
              >
                <div
                  className="framer-xzdbie hidden-123arm cards-s25 reveal"
                  data-framer-name="Track your spendings"
                  data-framer-component-type="RichTextContainer"
                >
                  <h3 dir="auto" className="framer-text cards-s70">
                    Full control. Built-in security.
                  </h3>
                </div>
                <div className="framer-a2vdbr" data-framer-name="Text Wrapper">
                  <div className="framer-5tcjk9">
                    <div className="framer-19vwvj">
                      <div
                        className="framer-avhx5q hidden-1u67xp5 reveal"
                        data-framer-name="Track your spendings"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h3 dir="auto" className="framer-text cards-s70">
                          Full control. Built-in security.
                        </h3>
                      </div>
                      <div
                        className="framer-1ldknob cards-s33 reveal"
                        data-framer-name="Automatically categorize your spending, track where your money goes, and gain insights into your habits."
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s76">
                          Know exactly where your money is going &mdash; with
                          smart categorization, real-time updates, and
                          easy-to-understand visuals that keep you informed
                          every step of the way.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="framer-h7e29a cards-s33">
                    <div className="framer-m3amiq-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Freeze or unfreeze your card instantly
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-2h2p0h-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Track every transaction as it happens
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-15atbpa-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Monitor usage across all your balances
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-1ulc994-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Stay protected with built-in security at every step
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="framer-dnuzij"
                  data-border="true"
                  data-framer-name="Mockup 2"
                >
                  <div className="framer-17ebt7m" data-framer-name="Image">
                    <div className="framer-16p6any">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="cards-s28"
                      >
                        <img
                          decoding="async"
                          width="499"
                          height="1024"
                          sizes="(min-width: 1440px) max(269.1644px, 100vw), (min-width: 810px) and (max-width: 1439.98px) max(269.1644px, max((max((100vw * 0.9975 - 80px) / 2.25, 1px) - 48px) * 0.7, 1px)), (max-width: 809.98px) max(max((100vw - 80px) * 0.7, 1px), 100vw)"
                          srcSet="/framerusercontent.com/images/XavmE8bUfvIT5ZIrBMvGdaz9s.png 499w"
                          src="/framerusercontent.com/images/XavmE8bUfvIT5ZIrBMvGdaz9s.png"
                          alt=""
                          className="cards-s75"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="framer-d7v9if hidden-123arm hidden-1u67xp5"
              data-framer-name="built in security"
            >
              <div className="framer-1fyi5bh" data-framer-name="Text Wrapper">
                <div className="framer-kznjng">
                  <div className="framer-i41yvs">
                    <div
                      className="framer-113bpf7 reveal"
                      data-framer-name="Track your spendings"
                      data-framer-component-type="RichTextContainer"
                    >
                      <h3 dir="auto" className="framer-text cards-s70">
                        Full control. Built-in security.
                      </h3>
                    </div>
                    <div className="ssr-variant hidden-do98mh">
                      <div
                        className="framer-16y7ilj reveal"
                        data-framer-name="Automatically categorize your spending, track where your money goes, and gain insights into your habits."
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s71">
                          Manage your card in real time with simple controls
                          designed to keep your money safe.
                        </p>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-123arm hidden-1u67xp5">
                      <div
                        className="framer-16y7ilj reveal"
                        data-framer-name="Automatically categorize your spending, track where your money goes, and gain insights into your habits."
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s76">
                          Manage your card in real time with simple controls
                          designed to keep your money safe.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="framer-117a6cv">
                  <div className="ssr-variant">
                    <div className="framer-185uqgf-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Freeze or unfreeze your card instantly
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant">
                    <div className="framer-1wkp0us-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Track every transaction as it happens
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant">
                    <div className="framer-qgajmj-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Monitor usage across all your balances
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant">
                    <div className="framer-z1o8iq-container">
                      <div
                        className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                        data-framer-name="Light"
                      >
                        <div
                          className="framer-ieoeb6"
                          data-framer-name="Icon Wrapper"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Icon"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-fwvc98 cards-s72"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 32 32" className="cards-s60">
                                <use href="#svg-1208994263_271" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-slphg9 cards-s73 reveal"
                          data-framer-name="Up to 100,00 Tracked Visits"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s74">
                            Stay protected with built-in security at every step
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="framer-181pxj9"
                data-border="true"
                data-framer-name="Mockup 2"
              >
                <div className="framer-nbxym2" data-framer-name="Image">
                  <div className="ssr-variant hidden-do98mh">
                    <div className="framer-1vugejv">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="cards-s28"
                      >
                        <img
                          decoding="async"
                          width="499"
                          height="1024"
                          srcSet="/framerusercontent.com/images/XavmE8bUfvIT5ZIrBMvGdaz9s.png 499w"
                          src="/framerusercontent.com/images/XavmE8bUfvIT5ZIrBMvGdaz9s.png"
                          alt=""
                          sizes="(min-width: 1440px) max(269.1644px, 100vw), (min-width: 810px) and (max-width: 1439.98px) max(269.1644px, max((max((100vw * 0.9975 - 80px) / 2.25, 1px) - 48px) * 0.7, 1px)), (max-width: 809.98px) max(max((100vw - 80px) * 0.7, 1px), 100vw)"
                          className="cards-s75"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-123arm hidden-1u67xp5">
                    <div className="framer-1vugejv">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="cards-s28"
                      >
                        <img
                          decoding="async"
                          width="499"
                          height="1024"
                          sizes="(min-width: 1440px) max(269.1644px, 100vw), (min-width: 810px) and (max-width: 1439.98px) max(269.1644px, max((max((100vw * 0.9975 - 80px) / 2.25, 1px) - 48px) * 0.7, 1px)), (max-width: 809.98px) max(max((100vw - 80px) * 0.7, 1px), 100vw)"
                          srcSet="/framerusercontent.com/images/XavmE8bUfvIT5ZIrBMvGdaz9s.png 499w"
                          src="/framerusercontent.com/images/XavmE8bUfvIT5ZIrBMvGdaz9s.png"
                          alt=""
                          className="cards-s75"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="ssr-variant hidden-do98mh hidden-1u67xp5">
              <div
                className="framer-13jnssr-container"
                data-framer-name="virtual card"
                id="cards"
                name="virtual card"
              >
                <div
                  name="virtual card"
                  className="framer-ozTYu framer-tx7Wz framer-Tt9kv framer-4zg99p framer-v-4pnkrm cards-s77"
                  data-framer-name="Variant 5"
                  data-highlight="true"
                  tabIndex={0}
                >
                  <div className="framer-k5mfe7" data-framer-name="Text">
                    <div
                      className="framer-3vwn4s cards-s78"
                      data-border="true"
                      data-framer-name="Frame 40889"
                    >
                      <div
                        className="framer-3725gh cards-s79 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p className="framer-text cards-s80">Virtual Cards</p>
                      </div>
                    </div>
                    <div
                      className="framer-1pbmtb3 cards-s81 reveal"
                      data-framer-name="Heading"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text cards-s82">
                        Create cards for anything
                      </p>
                    </div>
                    <div
                      className="framer-plgyeb cards-s83 reveal"
                      data-framer-name="Description"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text cards-s84">
                        Generate unlimited virtual cards for different uses.
                      </p>
                    </div>
                    {/*$*/}
                    <a
                      className="framer-t978tb framer-1jb0y36 cards-s85"
                      data-border="true"
                      data-framer-name="Button"
                      href="https://onelink.to/wch597"
                      target="_blank"
                      rel="noopener"
                    >
                      <div
                        className="framer-2wog1q cards-s86 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s87">
                          Create a virtual card
                        </p>
                      </div>
                    </a>
                    {/*/$*/}
                  </div>
                  <div
                    className="framer-ribg2m"
                    data-framer-name="Virtual Cards"
                  >
                    <div
                      className="framer-5puxpo cards-s2"
                      data-framer-name="Iphone base"
                    >
                      <div
                        data-framer-background-image-wrapper="true"
                        className="cards-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="2254"
                          height="2400"
                          sizes="(min-width: 1440px) 952.315px, (min-width: 810px) and (max-width: 1439.98px) 953px, (max-width: 809.98px) 725px"
                          srcSet="/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png 961w,/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png 1923w,/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png 2254w"
                          src="/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png"
                          alt=""
                          className="cards-s88"
                        />
                      </div>
                    </div>
                    <div className="framer-hhypyq">
                      <div className="framer-20m42c-container">
                        {/*$*/}
                        <section className="cards-s89">
                          <div className="cards-s90">
                            <div
                              className="vcard-marquee-track"
                              style={{
                                transform: `translateX(calc(var(--vcard-hold-offset, 0px) - var(--vcard-step) * ${vcardStep}))`,
                                transitionDuration: vcardInstant ? '0ms' : `${VCARD_SLIDE_MS}ms`,
                              }}
                            >
                            <ul className="cards-s91">
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                            </ul>
                            </div>
                          </div>
                          <fieldset
                            aria-label="Slideshow pagination controls"
                            className="framer--slideshow-controls cards-s94"
                          >
                            <div className="cards-s95">
                              <button
                                type="button"
                                aria-label="Previous"
                                tabIndex={0}
                                className="cards-s96"
                              >
                                <img
                                  decoding="async"
                                  width="40"
                                  height="40"
                                  src="/framerusercontent.com/images/6tTbkXggWgQCAJ4DO2QEdXXmgM.svg"
                                  alt="Back Arrow"
                                />
                              </button>
                              <button
                                type="button"
                                aria-label="Next"
                                tabIndex={0}
                                className="cards-s96"
                              >
                                <img
                                  decoding="async"
                                  width="40"
                                  height="40"
                                  src="/framerusercontent.com/images/11KSGbIZoRSg4pjdnUoif6MKHI.svg"
                                  alt="Next Arrow"
                                />
                              </button>
                            </div>
                          </fieldset>
                        </section>
                        {/*/$*/}
                      </div>
                      <div className="framer-wy2e0s" data-framer-name="Macd">
                        <div
                          className="framer-1j1mumj cards-s97"
                          data-framer-name="Rectangle 474"
                        />
                        <div
                          className="framer-1hjs0c6"
                          data-framer-name="Frame 1437256476"
                        >
                          <div
                            className="framer-13fqv39 cards-s98 reveal"
                            data-framer-name="\u20AC24 Sent to macd"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p className="framer-text cards-s99">
                              Online shopping card
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-1mzlw0d"
                          data-framer-name="Avatar Tag"
                        >
                          <div
                            className="framer-1d4n2xb cards-s100"
                            data-framer-name="Avatar"
                          >
                            <div
                              data-framer-background-image-wrapper="true"
                              className="cards-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="225"
                                height="225"
                                src="/framerusercontent.com/images/YY0ca5jbxvfncolpPESVSQYDts.png"
                                alt=""
                                className="cards-s101"
                              />
                            </div>
                            <div
                              className="framer-1wvl7ld cards-s102"
                              data-border="true"
                              data-framer-name="Picture \u2192 Amazon Logotype"
                            >
                              <div
                                data-framer-background-image-wrapper="true"
                                className="cards-s28"
                              >
                                <img
                                  decoding="async"
                                  loading="lazy"
                                  width="320"
                                  height="320"
                                  src="/framerusercontent.com/images/YlHsHiLlWM0hCGw1LWox6TDlU.png"
                                  alt=""
                                  className="cards-s88"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="framer-10gtitz"
                            data-framer-name="Tag"
                          >
                            <div
                              className="framer-1bm2hky cards-s103"
                              data-border="true"
                              data-framer-name="Ellipse 133"
                            />
                            <div
                              data-framer-component-type="SVG"
                              data-framer-name="Mask Group"
                              parentsize="0"
                              _constraints="[object Object]"
                              rotation="0"
                              shadows=""
                              className="framer-1q8vekd cards-s104"
                              aria-hidden="true"
                            >
                              <div className="svgContainer cards-s59">
                                <svg viewBox="0 0 16 16" className="cards-s60">
                                  <use href="#svg-484590915_1528" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-zn0vbg cards-s105"
                        data-framer-name="Arrow"
                      >
                        <div
                          className="framer-1padqzs"
                          data-framer-name="Arrow"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Group"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-1kku28x cards-s106"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 48 48" className="cards-s60">
                                <use href="#svg86067174_1236" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-rdnd3k cards-s107 reveal"
                        data-framer-name="pay for food \uD83E\uDD24"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p className="framer-text cards-s108">
                          <span className="cards-s109">
                            <span className="cards-s110">F</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">r</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">y</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">u</span>
                            <span className="cards-s110">r</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">w</span>
                            <span className="cards-s110">i</span>
                            <span className="cards-s110">f</span>
                            <span className="cards-s110">e</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">&#128131;</span>
                          </span>
                        </p>
                      </div>
                      <div className="framer-8348ab" data-framer-name="Macd">
                        <div
                          className="framer-17mfjzg cards-s97"
                          data-framer-name="Rectangle 474"
                        />
                        <div
                          className="framer-19qq06j"
                          data-framer-name="Frame 1437256476"
                        >
                          <div
                            className="framer-1u7hlnf cards-s98 reveal"
                            data-framer-name="\u20AC24 Sent to macd"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p className="framer-text cards-s99">
                              One time purchase
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-17hrqgz"
                          data-framer-name="Avatar Tag"
                        >
                          <div
                            className="framer-1akd9k3 cards-s100"
                            data-framer-name="Avatar"
                          >
                            <div
                              data-framer-background-image-wrapper="true"
                              className="cards-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="225"
                                height="225"
                                src="/framerusercontent.com/images/YY0ca5jbxvfncolpPESVSQYDts.png"
                                alt=""
                                className="cards-s101"
                              />
                            </div>
                          </div>
                          <div
                            className="framer-1vf1ygk"
                            data-framer-name="Tag"
                          >
                            <div
                              className="framer-1rcwywh cards-s103"
                              data-border="true"
                              data-framer-name="Ellipse 133"
                            />
                            <div
                              data-framer-component-type="SVG"
                              data-framer-name="Mask Group"
                              parentsize="0"
                              _constraints="[object Object]"
                              rotation="0"
                              shadows=""
                              className="framer-1h01zhm cards-s104"
                              aria-hidden="true"
                            >
                              <div className="svgContainer cards-s59">
                                <svg viewBox="0 0 16 16" className="cards-s60">
                                  <use href="#svg-484590915_1528" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1untca7 cards-s111"
                        data-framer-name="Arrow"
                      >
                        <div
                          className="framer-1xbrelm"
                          data-framer-name="Arrow"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Group"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-3kcr1l cards-s106"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 48 48" className="cards-s60">
                                <use href="#svg86067174_1236" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-zvkfyv cards-s107 reveal"
                        data-framer-name="pay for food \uD83E\uDD24"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p className="framer-text cards-s108">
                          <span className="cards-s109">
                            <span className="cards-s110">F</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">r</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">y</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">u</span>
                            <span className="cards-s110">r</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">f</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">d</span>
                            <span className="cards-s110">i</span>
                            <span className="cards-s110">e</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">s</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">n</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">&#129316;</span>
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="ssr-variant hidden-123arm hidden-1u67xp5">
              <div
                className="framer-13jnssr-container"
                data-framer-name="virtual card"
                id="cards"
                name="virtual card"
              >
                <div
                  name="virtual card"
                  className="framer-ozTYu framer-tx7Wz framer-Tt9kv framer-4zg99p framer-v-ktjqca cards-s112"
                  data-framer-name="Tablet"
                  data-highlight="true"
                  tabIndex={0}
                >
                  <div className="framer-k5mfe7" data-framer-name="Text">
                    <div
                      className="framer-3vwn4s cards-s78"
                      data-border="true"
                      data-framer-name="Frame 40889"
                    >
                      <div
                        className="framer-3725gh cards-s79 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p className="framer-text cards-s80">Virtual Cards</p>
                      </div>
                    </div>
                    <div
                      className="framer-1pbmtb3 cards-s81 reveal"
                      data-framer-name="Heading"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text cards-s82">
                        Create cards for
                      </p>
                      <p dir="auto" className="framer-text cards-s82">
                        anything
                      </p>
                    </div>
                    <div
                      className="framer-plgyeb cards-s83 reveal"
                      data-framer-name="Description"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text cards-s84">
                        Generate unlimited virtual cards for different uses.
                      </p>
                    </div>
                    {/*$*/}
                    <a
                      className="framer-t978tb framer-1jb0y36 cards-s85"
                      data-border="true"
                      data-framer-name="Button"
                      href="https://onelink.to/wch597"
                      target="_blank"
                      rel="noopener"
                    >
                      <div
                        className="framer-2wog1q cards-s86 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s87">
                          Create a virtual card
                        </p>
                      </div>
                    </a>
                    {/*/$*/}
                  </div>
                  <div
                    className="framer-ribg2m"
                    data-framer-name="Virtual Cards"
                  >
                    <div
                      className="framer-5puxpo"
                      data-framer-name="Iphone base"
                    >
                      <div
                        data-framer-background-image-wrapper="true"
                        className="cards-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="2254"
                          height="2400"
                          sizes="(min-width: 1440px) 952.315px, (min-width: 810px) and (max-width: 1439.98px) 953px, (max-width: 809.98px) 725px"
                          srcSet="/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png 961w,/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png 1923w,/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png 2254w"
                          src="/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png"
                          alt=""
                          className="cards-s88"
                        />
                      </div>
                    </div>
                    <div className="framer-hhypyq">
                      <div className="framer-20m42c-container">
                        {/*$*/}
                        <section className="cards-s89">
                          <div className="cards-s90">
                            <div
                              className="vcard-marquee-track"
                              style={{
                                transform: `translateX(calc(var(--vcard-hold-offset, 0px) - var(--vcard-step) * ${vcardStep}))`,
                                transitionDuration: vcardInstant ? '0ms' : `${VCARD_SLIDE_MS}ms`,
                              }}
                            >
                            <ul className="cards-s91">
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                            </ul>
                            </div>
                          </div>
                          <fieldset
                            aria-label="Slideshow pagination controls"
                            className="framer--slideshow-controls cards-s94"
                          >
                            <div className="cards-s95">
                              <button
                                type="button"
                                aria-label="Previous"
                                tabIndex={0}
                                className="cards-s96"
                              >
                                <img
                                  decoding="async"
                                  width="40"
                                  height="40"
                                  src="/framerusercontent.com/images/6tTbkXggWgQCAJ4DO2QEdXXmgM.svg"
                                  alt="Back Arrow"
                                />
                              </button>
                              <button
                                type="button"
                                aria-label="Next"
                                tabIndex={0}
                                className="cards-s96"
                              >
                                <img
                                  decoding="async"
                                  width="40"
                                  height="40"
                                  src="/framerusercontent.com/images/11KSGbIZoRSg4pjdnUoif6MKHI.svg"
                                  alt="Next Arrow"
                                />
                              </button>
                            </div>
                          </fieldset>
                        </section>
                        {/*/$*/}
                      </div>
                      <div className="framer-wy2e0s" data-framer-name="Macd">
                        <div
                          className="framer-1j1mumj cards-s97"
                          data-framer-name="Rectangle 474"
                        />
                        <div
                          className="framer-1hjs0c6"
                          data-framer-name="Frame 1437256476"
                        >
                          <div
                            className="framer-13fqv39 cards-s98 reveal"
                            data-framer-name="\u20AC24 Sent to macd"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p className="framer-text cards-s99">
                              Online shopping card
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-1mzlw0d"
                          data-framer-name="Avatar Tag"
                        >
                          <div
                            className="framer-1d4n2xb cards-s100"
                            data-framer-name="Avatar"
                          >
                            <div
                              data-framer-background-image-wrapper="true"
                              className="cards-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="225"
                                height="225"
                                src="/framerusercontent.com/images/YY0ca5jbxvfncolpPESVSQYDts.png"
                                alt=""
                                className="cards-s101"
                              />
                            </div>
                            <div
                              className="framer-1wvl7ld cards-s102"
                              data-border="true"
                              data-framer-name="Picture \u2192 Amazon Logotype"
                            >
                              <div
                                data-framer-background-image-wrapper="true"
                                className="cards-s28"
                              >
                                <img
                                  decoding="async"
                                  loading="lazy"
                                  width="320"
                                  height="320"
                                  src="/framerusercontent.com/images/YlHsHiLlWM0hCGw1LWox6TDlU.png"
                                  alt=""
                                  className="cards-s88"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="framer-10gtitz"
                            data-framer-name="Tag"
                          >
                            <div
                              className="framer-1bm2hky cards-s103"
                              data-border="true"
                              data-framer-name="Ellipse 133"
                            />
                            <div
                              data-framer-component-type="SVG"
                              data-framer-name="Mask Group"
                              parentsize="0"
                              _constraints="[object Object]"
                              rotation="0"
                              shadows=""
                              className="framer-1q8vekd cards-s104"
                              aria-hidden="true"
                            >
                              <div className="svgContainer cards-s59">
                                <svg viewBox="0 0 16 16" className="cards-s60">
                                  <use href="#svg-484590915_1528" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-zn0vbg cards-s113"
                        data-framer-name="Arrow"
                      >
                        <div
                          className="framer-1padqzs"
                          data-framer-name="Arrow"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Group"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-1kku28x cards-s106"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 48 48" className="cards-s60">
                                <use href="#svg86067174_1236" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-rdnd3k cards-s107 reveal"
                        data-framer-name="pay for food \uD83E\uDD24"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p className="framer-text cards-s108">
                          <span className="cards-s109">
                            <span className="cards-s110">F</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">r</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">y</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">u</span>
                            <span className="cards-s110">r</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">w</span>
                            <span className="cards-s110">i</span>
                            <span className="cards-s110">f</span>
                            <span className="cards-s110">e</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">&#128131;</span>
                          </span>
                        </p>
                      </div>
                      <div className="framer-8348ab" data-framer-name="Macd">
                        <div
                          className="framer-17mfjzg cards-s97"
                          data-framer-name="Rectangle 474"
                        />
                        <div
                          className="framer-19qq06j"
                          data-framer-name="Frame 1437256476"
                        >
                          <div
                            className="framer-1u7hlnf cards-s98 reveal"
                            data-framer-name="\u20AC24 Sent to macd"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p className="framer-text cards-s99">
                              One time purchase
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-17hrqgz"
                          data-framer-name="Avatar Tag"
                        >
                          <div
                            className="framer-1akd9k3 cards-s100"
                            data-framer-name="Avatar"
                          >
                            <div
                              data-framer-background-image-wrapper="true"
                              className="cards-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="225"
                                height="225"
                                src="/framerusercontent.com/images/YY0ca5jbxvfncolpPESVSQYDts.png"
                                alt=""
                                className="cards-s101"
                              />
                            </div>
                          </div>
                          <div
                            className="framer-1vf1ygk"
                            data-framer-name="Tag"
                          >
                            <div
                              className="framer-1rcwywh cards-s103"
                              data-border="true"
                              data-framer-name="Ellipse 133"
                            />
                            <div
                              data-framer-component-type="SVG"
                              data-framer-name="Mask Group"
                              parentsize="0"
                              _constraints="[object Object]"
                              rotation="0"
                              shadows=""
                              className="framer-1h01zhm cards-s104"
                              aria-hidden="true"
                            >
                              <div className="svgContainer cards-s59">
                                <svg viewBox="0 0 16 16" className="cards-s60">
                                  <use href="#svg-484590915_1528" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1untca7 cards-s114"
                        data-framer-name="Arrow"
                      >
                        <div
                          className="framer-1xbrelm"
                          data-framer-name="Arrow"
                        >
                          <div
                            data-framer-component-type="SVG"
                            data-framer-name="Group"
                            parentsize="0"
                            _constraints="[object Object]"
                            rotation="0"
                            shadows=""
                            className="framer-3kcr1l cards-s106"
                            aria-hidden="true"
                          >
                            <div className="svgContainer cards-s59">
                              <svg viewBox="0 0 48 48" className="cards-s60">
                                <use href="#svg86067174_1236" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-zvkfyv cards-s115 reveal"
                        data-framer-name="pay for food \uD83E\uDD24"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s108">
                          <span className="cards-s109">
                            <span className="cards-s110">F</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">r</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">y</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">u</span>
                            <span className="cards-s110">r</span>
                          </span>
                        </p>
                        <p dir="auto" className="framer-text cards-s108">
                          <span className="cards-s109">
                            <span className="cards-s110">f</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">d</span>
                            <span className="cards-s110">i</span>
                            <span className="cards-s110">e</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">s</span>
                            <span className="cards-s110">o</span>
                            <span className="cards-s110">n</span>
                          </span>{' '}
                          <span className="cards-s109">
                            <span className="cards-s110">&#129316;</span>
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="ssr-variant hidden-123arm hidden-do98mh">
              <div
                className="framer-13jnssr-container"
                data-framer-name="virtual card"
                id="cards"
                name="virtual card"
              >
                <div
                  name="virtual card"
                  className="framer-ozTYu framer-tx7Wz framer-Tt9kv framer-4zg99p framer-v-5w6nb5 cards-s116"
                  data-framer-name="InfiniteSection_Mobile"
                  data-highlight="true"
                  tabIndex={0}
                >
                  <div className="framer-k5mfe7" data-framer-name="Text">
                    <div
                      className="framer-3vwn4s cards-s78"
                      data-border="true"
                      data-framer-name="Frame 40889"
                    >
                      <div
                        className="framer-3725gh cards-s79 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p className="framer-text cards-s80">Virtual Cards</p>
                      </div>
                    </div>
                    <div
                      className="framer-1pbmtb3 cards-s81 reveal"
                      data-framer-name="Heading"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p className="framer-text cards-s117">
                        Infinite virtual cards
                      </p>
                    </div>
                    <div
                      className="framer-plgyeb cards-s83 reveal"
                      data-framer-name="Description"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p className="framer-text cards-s84">
                        Effortlessly create, use, and delete unlimited virtual
                        cards. Shop securely anywhere online, or easily share a
                        card with family for things like groceries.
                      </p>
                    </div>
                    {/*$*/}
                    <a
                      className="framer-t978tb framer-1jb0y36 cards-s85"
                      data-border="true"
                      data-framer-name="Button"
                      href="https://onelink.to/wch597"
                      target="_blank"
                      rel="noopener"
                    >
                      <div
                        className="framer-2wog1q cards-s86 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p className="framer-text cards-s87">
                          Create your first card
                        </p>
                      </div>
                    </a>
                    {/*/$*/}
                  </div>
                  <div
                    className="framer-ribg2m"
                    data-framer-name="Virtual Cards"
                  >
                    <div
                      className="framer-5puxpo cards-s118"
                      data-framer-name="Iphone base"
                    >
                      <div
                        data-framer-background-image-wrapper="true"
                        className="cards-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="2254"
                          height="2400"
                          sizes="(min-width: 1440px) 952.315px, (min-width: 810px) and (max-width: 1439.98px) 953px, (max-width: 809.98px) 725px"
                          srcSet="/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png 961w,/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png 1923w,/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png 2254w"
                          src="/framerusercontent.com/images/uk6Lkl4FZwvZS90VgrUmvGt6SWI.png"
                          alt=""
                          className="cards-s88"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-1fh7l3k cards-s119"
                      data-framer-name="iPhone 16 mockup portrait"
                    >
                      <div className="framer-vb3hw3" data-framer-name="main">
                        <div
                          data-framer-background-image-wrapper="true"
                          className="cards-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="1002"
                            height="2040"
                            sizes="(max-width: 809.98px) 336px"
                            srcSet="/framerusercontent.com/images/bMfPGaIKBbDIuUxkYcZ6Kcz5sk.png 1002w"
                            src="/framerusercontent.com/images/bMfPGaIKBbDIuUxkYcZ6Kcz5sk.png"
                            alt=""
                            className="cards-s29"
                          />
                        </div>
                      </div>
                      <div
                        className="framer-1to9gup"
                        data-framer-name="iPhone 16 mockup portrait label"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="cards-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="1802"
                            height="3909"
                            sizes="(max-width: 809.98px) 302.1317px"
                            srcSet="/framerusercontent.com/images/F2jWqIvYoQQDE8GiN09HdKfD92I.png 944w,/framerusercontent.com/images/F2jWqIvYoQQDE8GiN09HdKfD92I.png 1802w"
                            src="/framerusercontent.com/images/F2jWqIvYoQQDE8GiN09HdKfD92I.png"
                            alt=""
                            className="cards-s29"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="framer-hhypyq">
                      <div className="framer-20m42c-container">
                        {/*$*/}
                        <section className="cards-s89">
                          <div className="cards-s90">
                            <div
                              className="vcard-marquee-track"
                              style={{
                                transform: `translateX(calc(var(--vcard-hold-offset, 0px) - var(--vcard-step) * ${vcardStep}))`,
                                transitionDuration: vcardInstant ? '0ms' : `${VCARD_SLIDE_MS}ms`,
                              }}
                            >
                            <ul className="cards-s91">
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="false" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-jey85k cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 512w,/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png 759w"
                                      src="/framerusercontent.com/images/NdJlrwezFY0HH6Wdja7F8rVKlM.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1l9c9ts cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251.5px, (min-width: 810px) and (max-width: 1439.98px) 251.5px, (max-width: 809.98px) 251.5px"
                                      srcSet="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 512w,/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png 759w"
                                      src="/framerusercontent.com/images/KGLIiDdyVuzDEuEz2FIPmzlR56A.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-14bmszs cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                      srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                      src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                  <div
                                    className="framer-dj8coi"
                                    data-framer-name="Virtual Card new"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="cards-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="759"
                                        height="474"
                                        sizes="(min-width: 1440px) 251px, (min-width: 810px) and (max-width: 1439.98px) 251px, (max-width: 809.98px) 251px"
                                        srcSet="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 512w,/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png 759w"
                                        src="/framerusercontent.com/images/jy4OveZOubYHF7lLPjhwDWKmDsg.png"
                                        alt=""
                                        className="cards-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-1rurmn9 cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 512w,/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png 759w"
                                      src="/framerusercontent.com/images/5UQ1Hf5bVLwCwZJoexzMFaeXd28.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                              <li aria-hidden="true" className="cards-s92">
                                <div
                                  className="framer-19oda3l cards-s93"
                                  data-framer-name="Virtual Card"
                                >
                                  <div
                                    data-framer-background-image-wrapper="true"
                                    className="cards-s28"
                                  >
                                    <img
                                      decoding="async"
                                      width="759"
                                      height="474"
                                      sizes="(min-width: 1440px) 250px, (min-width: 810px) and (max-width: 1439.98px) 250px, (max-width: 809.98px) 250px"
                                      srcSet="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 512w,/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png 759w"
                                      src="/framerusercontent.com/images/3FTjlOujDDRivGNP0zLLPRYgnyg.png"
                                      alt=""
                                      className="cards-s88"
                                    />
                                  </div>
                                </div>
                              </li>
                            </ul>
                            </div>
                          </div>
                          <fieldset
                            aria-label="Slideshow pagination controls"
                            className="framer--slideshow-controls cards-s94"
                          >
                            <div className="cards-s95">
                              <button
                                type="button"
                                aria-label="Previous"
                                tabIndex={0}
                                className="cards-s96"
                              >
                                <img
                                  decoding="async"
                                  width="40"
                                  height="40"
                                  src="/framerusercontent.com/images/6tTbkXggWgQCAJ4DO2QEdXXmgM.svg"
                                  alt="Back Arrow"
                                />
                              </button>
                              <button
                                type="button"
                                aria-label="Next"
                                tabIndex={0}
                                className="cards-s96"
                              >
                                <img
                                  decoding="async"
                                  width="40"
                                  height="40"
                                  src="/framerusercontent.com/images/11KSGbIZoRSg4pjdnUoif6MKHI.svg"
                                  alt="Next Arrow"
                                />
                              </button>
                            </div>
                          </fieldset>
                        </section>
                        {/*/$*/}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="framer-pj9sft" data-framer-name="physical card">
              <div className="ssr-variant hidden-do98mh hidden-1u67xp5">
                <div className="framer-1k454s1-container">
                  <div
                    className="framer-Y4O6z framer-wt9p6f framer-v-1uzq0eg cards-s120"
                    data-framer-name="Desktop 2"
                  >
                    <div
                      className="framer-1oid3p1"
                      data-framer-name="Text Wrapper"
                    >
                      <div className="framer-xcmmsb">
                        <div className="framer-jkdvz4">
                          <div
                            className="framer-1tjpmfr cards-s78"
                            data-border="true"
                            data-framer-name="Frame 40889"
                          >
                            <div
                              className="framer-uvdwme cards-s79 reveal"
                              data-framer-name="Label"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s80">
                                Physical Cards
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="framer-ihqcf6">
                          <div
                            className="framer-ekwedi cards-s121 reveal"
                            data-framer-name="Automatically categorize your spending, track where your money goes, and gain insights into your habits."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p dir="auto" className="framer-text cards-s122">
                              A physical card for everyday spending, wherever
                              you are.
                            </p>
                          </div>
                          <div
                            className="framer-452d4t cards-s123 reveal"
                            data-framer-name="Track your spendings"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h3 dir="auto" className="framer-text cards-s124">
                              When digital isn&rsquo;t enough
                            </h3>
                          </div>
                        </div>
                      </div>
                      <div className="framer-1uacqpa">
                        <div className="framer-1869itf-container">
                          <div
                            className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                            data-framer-name="Light"
                          >
                            <div
                              className="framer-ieoeb6"
                              data-framer-name="Icon Wrapper"
                            >
                              <div
                                data-framer-component-type="SVG"
                                data-framer-name="Icon"
                                parentsize="0"
                                _constraints="[object Object]"
                                rotation="0"
                                shadows=""
                                className="framer-fwvc98 cards-s72"
                                aria-hidden="true"
                              >
                                <div className="svgContainer cards-s59">
                                  <svg
                                    viewBox="0 0 32 32"
                                    className="cards-s60"
                                  >
                                    <use href="#svg-1208994263_271" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-slphg9 cards-s73 reveal"
                              data-framer-name="Up to 100,00 Tracked Visits"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s74">
                                Pay in-store anywhere cards are accepted
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="framer-10tve4v-container">
                          <div
                            className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                            data-framer-name="Light"
                          >
                            <div
                              className="framer-ieoeb6"
                              data-framer-name="Icon Wrapper"
                            >
                              <div
                                data-framer-component-type="SVG"
                                data-framer-name="Icon"
                                parentsize="0"
                                _constraints="[object Object]"
                                rotation="0"
                                shadows=""
                                className="framer-fwvc98 cards-s72"
                                aria-hidden="true"
                              >
                                <div className="svgContainer cards-s59">
                                  <svg
                                    viewBox="0 0 32 32"
                                    className="cards-s60"
                                  >
                                    <use href="#svg-1208994263_271" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-slphg9 cards-s73 reveal"
                              data-framer-name="Up to 100,00 Tracked Visits"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s74">
                                Use contactless or add to Apple Pay and Google
                                Pay
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="framer-476oou-container">
                          <div
                            className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                            data-framer-name="Light"
                          >
                            <div
                              className="framer-ieoeb6"
                              data-framer-name="Icon Wrapper"
                            >
                              <div
                                data-framer-component-type="SVG"
                                data-framer-name="Icon"
                                parentsize="0"
                                _constraints="[object Object]"
                                rotation="0"
                                shadows=""
                                className="framer-fwvc98 cards-s72"
                                aria-hidden="true"
                              >
                                <div className="svgContainer cards-s59">
                                  <svg
                                    viewBox="0 0 32 32"
                                    className="cards-s60"
                                  >
                                    <use href="#svg-1208994263_271" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-slphg9 cards-s73 reveal"
                              data-framer-name="Up to 100,00 Tracked Visits"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s74">
                                Top up from any balance in your account
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="framer-1hwgsd6-container">
                          <div
                            className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                            data-framer-name="Light"
                          >
                            <div
                              className="framer-ieoeb6"
                              data-framer-name="Icon Wrapper"
                            >
                              <div
                                data-framer-component-type="SVG"
                                data-framer-name="Icon"
                                parentsize="0"
                                _constraints="[object Object]"
                                rotation="0"
                                shadows=""
                                className="framer-fwvc98 cards-s72"
                                aria-hidden="true"
                              >
                                <div className="svgContainer cards-s59">
                                  <svg
                                    viewBox="0 0 32 32"
                                    className="cards-s60"
                                  >
                                    <use href="#svg-1208994263_271" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-slphg9 cards-s73 reveal"
                              data-framer-name="Up to 100,00 Tracked Visits"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s74">
                                One card for everyday spending
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-13z264z cards-s125"
                        data-framer-name="Item"
                      >
                        <div
                          className="framer-diw22z cards-s126 reveal"
                          data-framer-name="Label"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s127">
                            Coming soon
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-7u1gb-container">
                      <div
                        className="framer-LWdnI framer-1xp2h1v framer-v-1xp2h1v cards-s128"
                        data-border="true"
                        data-framer-name="With Mask"
                      >
                        <div
                          className="framer-14n2ahs cards-s129"
                          data-framer-name="Image"
                        >
                          <div className="framer-9yzt8b cards-s130">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="cards-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max((max((100vw - 304px) / 2.25, 1px) - 48px) * 0.7, 1px), (min-width: 810px) and (max-width: 1439.98px) max((max((770px - 80px) / 2.25, 1px) - 48px) * 0.7, 1px), (max-width: 809.98px) max((calc(100vw - 32px) - 48px) * 0.7, 1px)"
                                srcSet="/framerusercontent.com/images/kz5Z5lDCJCQA60wEuNC3FhvpIcM.png 499w"
                                src="/framerusercontent.com/images/kz5Z5lDCJCQA60wEuNC3FhvpIcM.png"
                                alt=""
                                className="cards-s75"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ssr-variant hidden-123arm hidden-1u67xp5">
                <div className="framer-1k454s1-container">
                  <div
                    className="framer-Y4O6z framer-wt9p6f framer-v-1jor32z cards-s120"
                    data-framer-name="Tablet 1"
                  >
                    <div
                      className="framer-1oid3p1"
                      data-framer-name="Text Wrapper"
                    >
                      <div className="framer-xcmmsb">
                        <div className="framer-ihqcf6">
                          <div
                            className="framer-ekwedi cards-s121 reveal"
                            data-framer-name="Automatically categorize your spending, track where your money goes, and gain insights into your habits."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p dir="auto" className="framer-text cards-s131">
                              A physical card for everyday spending, wherever
                              you are.
                            </p>
                          </div>
                          <div
                            className="framer-wt9dpe cards-s123 reveal"
                            data-framer-name="Track your spendings"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h3 dir="auto" className="framer-text cards-s124">
                              When digital isn&rsquo;t enough
                            </h3>
                          </div>
                          <div
                            className="framer-ynieqa cards-s78"
                            data-border="true"
                            data-framer-name="Frame 40889"
                          >
                            <div
                              className="framer-198totm cards-s79 reveal"
                              data-framer-name="Label"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s80">
                                Physical Cards
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="framer-1uacqpa">
                        <div className="framer-1869itf-container">
                          <div
                            className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                            data-framer-name="Light"
                          >
                            <div
                              className="framer-ieoeb6"
                              data-framer-name="Icon Wrapper"
                            >
                              <div
                                data-framer-component-type="SVG"
                                data-framer-name="Icon"
                                parentsize="0"
                                _constraints="[object Object]"
                                rotation="0"
                                shadows=""
                                className="framer-fwvc98 cards-s132"
                                aria-hidden="true"
                              >
                                <div className="svgContainer cards-s59">
                                  <svg
                                    viewBox="0 0 32 32"
                                    className="cards-s60"
                                  >
                                    <use href="#svg-1208994263_271" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-slphg9 cards-s73 reveal"
                              data-framer-name="Up to 100,00 Tracked Visits"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s74">
                                Pay in-store anywhere cards are accepted
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="framer-10tve4v-container">
                          <div
                            className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                            data-framer-name="Light"
                          >
                            <div
                              className="framer-ieoeb6"
                              data-framer-name="Icon Wrapper"
                            >
                              <div
                                data-framer-component-type="SVG"
                                data-framer-name="Icon"
                                parentsize="0"
                                _constraints="[object Object]"
                                rotation="0"
                                shadows=""
                                className="framer-fwvc98 cards-s132"
                                aria-hidden="true"
                              >
                                <div className="svgContainer cards-s59">
                                  <svg
                                    viewBox="0 0 32 32"
                                    className="cards-s60"
                                  >
                                    <use href="#svg-1208994263_271" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-slphg9 cards-s73 reveal"
                              data-framer-name="Up to 100,00 Tracked Visits"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s74">
                                Use contactless or add to Apple Pay and Google
                                Pay
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="framer-476oou-container">
                          <div
                            className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                            data-framer-name="Light"
                          >
                            <div
                              className="framer-ieoeb6"
                              data-framer-name="Icon Wrapper"
                            >
                              <div
                                data-framer-component-type="SVG"
                                data-framer-name="Icon"
                                parentsize="0"
                                _constraints="[object Object]"
                                rotation="0"
                                shadows=""
                                className="framer-fwvc98 cards-s132"
                                aria-hidden="true"
                              >
                                <div className="svgContainer cards-s59">
                                  <svg
                                    viewBox="0 0 32 32"
                                    className="cards-s60"
                                  >
                                    <use href="#svg-1208994263_271" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-slphg9 cards-s73 reveal"
                              data-framer-name="Up to 100,00 Tracked Visits"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s74">
                                Top up from any balance in your account
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="framer-7u1gb-container">
                      <div
                        className="framer-LWdnI framer-1xp2h1v framer-v-1xp2h1v cards-s133"
                        data-border="true"
                        data-framer-name="With Mask"
                      >
                        <div
                          className="framer-14n2ahs cards-s129"
                          data-framer-name="Image"
                        >
                          <div className="framer-9yzt8b cards-s130">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="cards-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max((max((100vw - 304px) / 2.25, 1px) - 48px) * 0.7, 1px), (min-width: 810px) and (max-width: 1439.98px) max((max((770px - 80px) / 2.25, 1px) - 48px) * 0.7, 1px), (max-width: 809.98px) max((calc(100vw - 32px) - 48px) * 0.7, 1px)"
                                srcSet="/framerusercontent.com/images/kz5Z5lDCJCQA60wEuNC3FhvpIcM.png 499w"
                                src="/framerusercontent.com/images/kz5Z5lDCJCQA60wEuNC3FhvpIcM.png"
                                alt=""
                                className="cards-s75"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ssr-variant hidden-123arm hidden-do98mh">
                <div className="framer-1k454s1-container">
                  <div
                    className="framer-Y4O6z framer-wt9p6f framer-v-n99h54 cards-s134"
                    data-framer-name="Mobile"
                  >
                    <div
                      className="framer-ezj3qz cards-s135 reveal"
                      data-framer-name="Track your spendings"
                      data-framer-component-type="RichTextContainer"
                    >
                      <h3 dir="auto" className="framer-text cards-s136">
                        When digital isn&rsquo;t enough
                      </h3>
                    </div>
                    <div
                      className="framer-1oid3p1"
                      data-framer-name="Text Wrapper"
                    >
                      <div className="framer-xcmmsb">
                        <div className="framer-ihqcf6">
                          <div
                            className="framer-ekwedi cards-s137 reveal"
                            data-framer-name="Automatically categorize your spending, track where your money goes, and gain insights into your habits."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p dir="auto" className="framer-text cards-s138">
                              A physical card for everyday spending, wherever
                              you are.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="framer-1uacqpa cards-s33">
                        <div className="framer-1869itf-container">
                          <div
                            className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                            data-framer-name="Light"
                          >
                            <div
                              className="framer-ieoeb6"
                              data-framer-name="Icon Wrapper"
                            >
                              <div
                                data-framer-component-type="SVG"
                                data-framer-name="Icon"
                                parentsize="0"
                                _constraints="[object Object]"
                                rotation="0"
                                shadows=""
                                className="framer-fwvc98 cards-s132"
                                aria-hidden="true"
                              >
                                <div className="svgContainer cards-s59">
                                  <svg
                                    viewBox="0 0 32 32"
                                    className="cards-s60"
                                  >
                                    <use href="#svg-1208994263_271" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-slphg9 cards-s73 reveal"
                              data-framer-name="Up to 100,00 Tracked Visits"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s74">
                                Pay in-store anywhere cards are accepted
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="framer-10tve4v-container">
                          <div
                            className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                            data-framer-name="Light"
                          >
                            <div
                              className="framer-ieoeb6"
                              data-framer-name="Icon Wrapper"
                            >
                              <div
                                data-framer-component-type="SVG"
                                data-framer-name="Icon"
                                parentsize="0"
                                _constraints="[object Object]"
                                rotation="0"
                                shadows=""
                                className="framer-fwvc98 cards-s132"
                                aria-hidden="true"
                              >
                                <div className="svgContainer cards-s59">
                                  <svg
                                    viewBox="0 0 32 32"
                                    className="cards-s60"
                                  >
                                    <use href="#svg-1208994263_271" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-slphg9 cards-s73 reveal"
                              data-framer-name="Up to 100,00 Tracked Visits"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s74">
                                Use contactless or add to Apple Pay and Google
                                Pay
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="framer-476oou-container">
                          <div
                            className="framer-lj12W framer-1v21ce7 framer-v-1v21ce7 cards-s3"
                            data-framer-name="Light"
                          >
                            <div
                              className="framer-ieoeb6"
                              data-framer-name="Icon Wrapper"
                            >
                              <div
                                data-framer-component-type="SVG"
                                data-framer-name="Icon"
                                parentsize="0"
                                _constraints="[object Object]"
                                rotation="0"
                                shadows=""
                                className="framer-fwvc98 cards-s132"
                                aria-hidden="true"
                              >
                                <div className="svgContainer cards-s59">
                                  <svg
                                    viewBox="0 0 32 32"
                                    className="cards-s60"
                                  >
                                    <use href="#svg-1208994263_271" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-slphg9 cards-s73 reveal"
                              data-framer-name="Up to 100,00 Tracked Visits"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p dir="auto" className="framer-text cards-s74">
                                Top up from any balance in your account
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="framer-7u1gb-container">
                      <div
                        className="framer-LWdnI framer-1xp2h1v framer-v-1xp2h1v cards-s139"
                        data-border="true"
                        data-framer-name="With Mask"
                      >
                        <div
                          className="framer-14n2ahs cards-s129"
                          data-framer-name="Image"
                        >
                          <div className="framer-9yzt8b cards-s130">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="cards-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max((max((100vw - 304px) / 2.25, 1px) - 48px) * 0.7, 1px), (min-width: 810px) and (max-width: 1439.98px) max((max((770px - 80px) / 2.25, 1px) - 48px) * 0.7, 1px), (max-width: 809.98px) max((calc(100vw - 32px) - 48px) * 0.7, 1px)"
                                srcSet="/framerusercontent.com/images/kz5Z5lDCJCQA60wEuNC3FhvpIcM.png 499w"
                                src="/framerusercontent.com/images/kz5Z5lDCJCQA60wEuNC3FhvpIcM.png"
                                alt=""
                                className="cards-s75"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="framer-15ja71q" data-framer-name="metal card">
              <div className="framer-8imttp" data-framer-name="Section">
                <div className="framer-fho5h" data-framer-name="Content">
                  <div className="framer-o41kkn" data-framer-name="Container">
                    <div
                      className="framer-g5giy0"
                      data-border="true"
                      data-framer-name="Frame 40889"
                    >
                      <div
                        className="framer-xzimqf cards-s25 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p className="framer-text cards-s32">Metal Cards</p>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-1u67xp5">
                      <div
                        className="framer-vx6izs reveal"
                        data-framer-name="Scale faster"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h2 dir="auto" className="framer-text cards-s140">
                          Upgrade your experience
                        </h2>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-123arm hidden-do98mh">
                      <div
                        className="framer-vx6izs reveal"
                        data-framer-name="Scale faster"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h2 dir="auto" className="framer-text cards-s141">
                          Upgrade your experience
                        </h2>
                      </div>
                    </div>
                    <div
                      className="framer-1t3bm9m reveal"
                      data-framer-name="Powerful, self-serve product and growth analytics to help you convert, engage, and retain more."
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text cards-s35">
                        Premium metal card with added benefits and services.
                      </p>
                    </div>
                  </div>
                  <div className="framer-otqxfg" data-framer-name="Container">
                    <div
                      className="framer-1dei3lx"
                      data-framer-name="Container"
                    >
                      <div
                        className="framer-qj17gq"
                        data-framer-name="Container"
                      >
                        <div
                          className="framer-1hu18r7"
                          data-framer-name="Container"
                        >
                          <div
                            className="framer-dh4upj"
                            data-framer-name="Heading + Badge"
                          >
                            <div
                              className="framer-sy7isq"
                              data-framer-name="Item"
                            >
                              <div
                                className="framer-e4xjsf cards-s25 reveal"
                                data-framer-name="Label"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p
                                  dir="auto"
                                  className="framer-text cards-s142"
                                >
                                  Coming soon
                                </p>
                              </div>
                            </div>
                            <div className="ssr-variant hidden-1u67xp5">
                              <div
                                className="framer-qxabhu cards-s37 reveal"
                                data-framer-name="Automate Meeting Scheduling"
                                data-framer-component-type="RichTextContainer"
                              >
                                <h3 className="framer-text cards-s143">
                                  Luxury perks with metal
                                </h3>
                              </div>
                            </div>
                            <div className="ssr-variant hidden-123arm hidden-do98mh">
                              <div
                                className="framer-qxabhu cards-s37 reveal"
                                data-framer-name="Automate Meeting Scheduling"
                                data-framer-component-type="RichTextContainer"
                              >
                                <h3 className="framer-text cards-s144">
                                  Luxury perks with metal
                                </h3>
                              </div>
                            </div>
                            <div
                              className="framer-1uqleoa"
                              data-framer-name="Frame 17"
                            >
                              <div
                                className="framer-2bq6z"
                                data-framer-name="Frame 11"
                              >
                                <div
                                  data-framer-component-type="SVG"
                                  data-framer-name="SVG \u2192 SVG"
                                  parentsize="0"
                                  _constraints="[object Object]"
                                  rotation="0"
                                  shadows=""
                                  className="framer-rt2f7i cards-s104"
                                  aria-hidden="true"
                                >
                                  <div className="svgContainer cards-s59">
                                    <svg
                                      viewBox="0 0 18 18"
                                      className="cards-s60"
                                    >
                                      <use href="#svg-458407791_470" />
                                    </svg>
                                  </div>
                                </div>
                                <div
                                  className="framer-c1m0n8 cards-s37 reveal"
                                  data-framer-name="EUR / GBP IBAN accounts \u2192 Scallop Airport Companion"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p className="framer-text cards-s145">
                                    <span className="framer-text cards-s146">
                                      Scallop Airport Companion
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div
                                className="framer-1k7fjw1"
                                data-framer-name="Frame 12"
                              >
                                <div
                                  data-framer-component-type="SVG"
                                  data-framer-name="SVG \u2192 SVG"
                                  parentsize="0"
                                  _constraints="[object Object]"
                                  rotation="0"
                                  shadows=""
                                  className="framer-1a42j1q cards-s104"
                                  aria-hidden="true"
                                >
                                  <div className="svgContainer cards-s59">
                                    <svg
                                      viewBox="0 0 18 18"
                                      className="cards-s60"
                                    >
                                      <use href="#svg-458407791_470" />
                                    </svg>
                                  </div>
                                </div>
                                <div
                                  className="framer-hydv87 cards-s37"
                                  data-framer-name="Swap crypto <-> fiat seamlessly \u2192 24 X 7  365 Days Personal Assistant"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p className="framer-text cards-s145">
                                    <span className="framer-text cards-s146">
                                      24 X 7 365 Days Personal Assistant
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div
                                className="framer-1u1w4wv"
                                data-framer-name="Frame 14"
                              >
                                <div
                                  data-framer-component-type="SVG"
                                  data-framer-name="SVG \u2192 SVG"
                                  parentsize="0"
                                  _constraints="[object Object]"
                                  rotation="0"
                                  shadows=""
                                  className="framer-7fpy6s cards-s104"
                                  aria-hidden="true"
                                >
                                  <div className="svgContainer cards-s59">
                                    <svg
                                      viewBox="0 0 18 18"
                                      className="cards-s60"
                                    >
                                      <use href="#svg-458407791_470" />
                                    </svg>
                                  </div>
                                </div>
                                <div
                                  className="framer-1fwbi77 cards-s37 reveal"
                                  data-framer-name="EAL6+ security & 24/7 support \u2192 Travel & Purchase Protect"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p className="framer-text cards-s145">
                                    <span className="framer-text cards-s146">
                                      Travel &amp; Purchase Protect
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div
                                className="framer-pum63k"
                                data-framer-name="Frame 15"
                              >
                                <div
                                  data-framer-component-type="SVG"
                                  data-framer-name="SVG \u2192 SVG"
                                  parentsize="0"
                                  _constraints="[object Object]"
                                  rotation="0"
                                  shadows=""
                                  className="framer-tqqtp9 cards-s104"
                                  aria-hidden="true"
                                >
                                  <div className="svgContainer cards-s59">
                                    <svg
                                      viewBox="0 0 18 18"
                                      className="cards-s60"
                                    >
                                      <use href="#svg-458407791_470" />
                                    </svg>
                                  </div>
                                </div>
                                <div
                                  className="framer-1rxphu0 cards-s37 reveal"
                                  data-framer-name="Instant KYC onboarding \u2192 Luxury Hotel Collection"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p className="framer-text cards-s145">
                                    <span className="framer-text cards-s146">
                                      Luxury Hotel Collection
                                    </span>
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1o0zrn1"
                        data-framer-name="Container"
                      >
                        <div className="ssr-variant hidden-do98mh">
                          <div className="framer-cfcnxl-container cards-s147">
                            <div
                              className={`framer-PnI6b framer-j5vylj framer-v-j5vylj${metalCardOut ? ' metal-card-out' : ''}`}
                              data-framer-name={metalCardOut ? 'Selected' : 'Default'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              aria-pressed={metalCardOut}
                              aria-label="Metal card — activate to slide it out"
                              onClick={toggleMetalCard}
                              onKeyDown={onMetalCardKeyDown}
                            >
                              <div
                                className="framer-gewgyh cards-s25"
                                data-framer-name="Front"
                                draggable={false}
                              >
                                <div
                                  data-framer-background-image-wrapper="true"
                                  className="cards-s28"
                                >
                                  <img
                                    decoding="async"
                                    loading="lazy"
                                    width="1024"
                                    height="1536"
                                    sizes="(min-width: 1440px) 276px, (min-width: 810px) and (max-width: 1439.98px) 276px, (max-width: 809.98px) 276px"
                                    srcSet="/framerusercontent.com/images/Sp1hfgUahyG4FzGFHI3mJwfHoDg.png 682w,/framerusercontent.com/images/Sp1hfgUahyG4FzGFHI3mJwfHoDg.png 1024w"
                                    src="/framerusercontent.com/images/Sp1hfgUahyG4FzGFHI3mJwfHoDg.png"
                                    alt=""
                                    draggable={false}
                                    className="cards-s29"
                                  />
                                </div>
                                <div
                                  data-framer-component-type="SVG"
                                  data-framer-name="icone"
                                  parentsize="0"
                                  _constraints="[object Object]"
                                  rotation="0"
                                  shadows=""
                                  className="framer-n9uqc6 cards-s148"
                                  aria-hidden="true"
                                >
                                  <div className="svgContainer cards-s59">
                                    <svg
                                      viewBox="0 0 196 196"
                                      className="cards-s60"
                                    >
                                      <use href="#svg829552244_2744" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              <div
                                className="framer-101vuox cards-s149"
                                data-framer-name="Back"
                                draggable={false}
                              >
                                <div
                                  data-framer-background-image-wrapper="true"
                                  className="cards-s28"
                                >
                                  <img
                                    decoding="async"
                                    loading="lazy"
                                    width="354"
                                    height="530"
                                    sizes="(min-width: 1440px) 270px, (min-width: 810px) and (max-width: 1439.98px) 270px, (max-width: 809.98px) 270px"
                                    srcSet="/framerusercontent.com/images/Ur85VDpW7evQHVIAuypuWUYt0.png 354w"
                                    src="/framerusercontent.com/images/Ur85VDpW7evQHVIAuypuWUYt0.png"
                                    alt=""
                                    draggable={false}
                                    className="cards-s29"
                                  />
                                </div>
                                <div
                                  className="framer-1t8ekp8 cards-s150"
                                  data-framer-name="Shadow"
                                />
                              </div>
                              <div
                                className="framer-1oc9pod-container cards-s25"
                                data-framer-name="Metal Card"
                                name="Metal Card"
                              >
                                {/*$*/}
                                <div className="cards-s151">
                                  <div
                                    className="framer-epdmfj cards-s152"
                                    data-framer-name="Metal card"
                                    draggable={false}
                                  >
                                    <div
                                      className="framer-ds4i41"
                                      data-framer-name="Black Card"
                                    >
                                      <div
                                        data-framer-background-image-wrapper="true"
                                        className="cards-s28"
                                      >
                                        <img
                                          decoding="async"
                                          width="860"
                                          height="1360"
                                          sizes="(min-width: 1440px) 216px, (min-width: 810px) and (max-width: 1439.98px) 216px, (max-width: 809.98px) 216px"
                                          srcSet="/framerusercontent.com/images/S9GKpMin59QPzrZAhw4lOEkLXY.png 647w,/framerusercontent.com/images/S9GKpMin59QPzrZAhw4lOEkLXY.png 860w"
                                          src="/framerusercontent.com/images/S9GKpMin59QPzrZAhw4lOEkLXY.png"
                                          alt=""
                                          className="cards-s29"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {/*/$*/}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="ssr-variant hidden-123arm hidden-1u67xp5">
                          <div className="framer-cfcnxl-container cards-s2">
                            <div
                              className={`framer-PnI6b framer-j5vylj framer-v-j5vylj${metalCardOut ? ' metal-card-out' : ''}`}
                              data-framer-name={metalCardOut ? 'Selected' : 'Default'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              aria-pressed={metalCardOut}
                              aria-label="Metal card — activate to slide it out"
                              onClick={toggleMetalCard}
                              onKeyDown={onMetalCardKeyDown}
                            >
                              <div
                                className="framer-gewgyh cards-s25"
                                data-framer-name="Front"
                                draggable={false}
                              >
                                <div
                                  data-framer-background-image-wrapper="true"
                                  className="cards-s28"
                                >
                                  <img
                                    decoding="async"
                                    loading="lazy"
                                    width="1024"
                                    height="1536"
                                    sizes="(min-width: 1440px) 276px, (min-width: 810px) and (max-width: 1439.98px) 276px, (max-width: 809.98px) 276px"
                                    srcSet="/framerusercontent.com/images/Sp1hfgUahyG4FzGFHI3mJwfHoDg.png 682w,/framerusercontent.com/images/Sp1hfgUahyG4FzGFHI3mJwfHoDg.png 1024w"
                                    src="/framerusercontent.com/images/Sp1hfgUahyG4FzGFHI3mJwfHoDg.png"
                                    alt=""
                                    draggable={false}
                                    className="cards-s29"
                                  />
                                </div>
                                <div
                                  data-framer-component-type="SVG"
                                  data-framer-name="icone"
                                  parentsize="0"
                                  _constraints="[object Object]"
                                  rotation="0"
                                  shadows=""
                                  className="framer-n9uqc6 cards-s148"
                                  aria-hidden="true"
                                >
                                  <div className="svgContainer cards-s59">
                                    <svg
                                      viewBox="0 0 196 196"
                                      className="cards-s60"
                                    >
                                      <use href="#svg829552244_2744" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              <div
                                className="framer-101vuox cards-s149"
                                data-framer-name="Back"
                                draggable={false}
                              >
                                <div
                                  data-framer-background-image-wrapper="true"
                                  className="cards-s28"
                                >
                                  <img
                                    decoding="async"
                                    loading="lazy"
                                    width="354"
                                    height="530"
                                    sizes="(min-width: 1440px) 270px, (min-width: 810px) and (max-width: 1439.98px) 270px, (max-width: 809.98px) 270px"
                                    srcSet="/framerusercontent.com/images/Ur85VDpW7evQHVIAuypuWUYt0.png 354w"
                                    src="/framerusercontent.com/images/Ur85VDpW7evQHVIAuypuWUYt0.png"
                                    alt=""
                                    draggable={false}
                                    className="cards-s29"
                                  />
                                </div>
                                <div
                                  className="framer-1t8ekp8 cards-s150"
                                  data-framer-name="Shadow"
                                />
                              </div>
                              <div
                                className="framer-1oc9pod-container cards-s25"
                                data-framer-name="Metal Card"
                                name="Metal Card"
                              >
                                {/*$*/}
                                <div className="cards-s151">
                                  <div
                                    className="framer-epdmfj cards-s152"
                                    data-framer-name="Metal card"
                                    draggable={false}
                                  >
                                    <div
                                      className="framer-ds4i41"
                                      data-framer-name="Black Card"
                                    >
                                      <div
                                        data-framer-background-image-wrapper="true"
                                        className="cards-s28"
                                      >
                                        <img
                                          decoding="async"
                                          width="860"
                                          height="1360"
                                          sizes="(min-width: 1440px) 216px, (min-width: 810px) and (max-width: 1439.98px) 216px, (max-width: 809.98px) 216px"
                                          srcSet="/framerusercontent.com/images/S9GKpMin59QPzrZAhw4lOEkLXY.png 647w,/framerusercontent.com/images/S9GKpMin59QPzrZAhw4lOEkLXY.png 860w"
                                          src="/framerusercontent.com/images/S9GKpMin59QPzrZAhw4lOEkLXY.png"
                                          alt=""
                                          className="cards-s29"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {/*/$*/}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-x5sset hidden-1u67xp5 cards-s153 reveal"
                          data-framer-name="onetime use card for ur aunty\u2019s trip"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s154">
                            <span className="cards-s109">
                              <span className="cards-s110">C</span>
                              <span className="cards-s110">l</span>
                              <span className="cards-s110">i</span>
                              <span className="cards-s110">c</span>
                              <span className="cards-s110">k</span>
                            </span>{' '}
                            <span className="cards-s109">
                              <span className="cards-s110">t</span>
                              <span className="cards-s110">h</span>
                              <span className="cards-s110">e</span>
                            </span>{' '}
                            <span className="cards-s109">
                              <span className="cards-s110">c</span>
                              <span className="cards-s110">a</span>
                              <span className="cards-s110">r</span>
                              <span className="cards-s110">d</span>
                            </span>{' '}
                            <span className="cards-s109" />
                          </p>
                          <p dir="auto" className="framer-text cards-s154">
                            <span className="cards-s109">
                              <span className="cards-s110">t</span>
                              <span className="cards-s110">o</span>
                            </span>{' '}
                            <span className="cards-s109">
                              <span className="cards-s110">s</span>
                              <span className="cards-s110">e</span>
                              <span className="cards-s110">e</span>
                            </span>{' '}
                            <span className="cards-s109" />
                          </p>
                          <p dir="auto" className="framer-text cards-s154">
                            <span className="cards-s109">
                              <span className="cards-s110">t</span>
                              <span className="cards-s110">h</span>
                              <span className="cards-s110">e</span>
                            </span>{' '}
                            <span className="cards-s109">
                              <span className="cards-s110">m</span>
                              <span className="cards-s110">a</span>
                              <span className="cards-s110">g</span>
                              <span className="cards-s110">i</span>
                              <span className="cards-s110">c</span>
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="framer-fn8m2e"
              data-framer-name="cards slider"
              id="trigger-lock-1"
            >
              <div className="framer-1fb9arm" data-framer-name="Hero text">
                <div
                  className="framer-1qaisk0"
                  data-framer-name="Frame 1400001633"
                >
                  <div className="ssr-variant hidden-1u67xp5">
                    <div
                      className="framer-j85i0t reveal"
                      data-framer-name="Scale faster"
                      data-framer-component-type="RichTextContainer"
                    >
                      <h2 dir="auto" className="framer-text cards-s155">
                        Your balance. Everywhere
                        <br className="framer-text" />
                        you pay.
                      </h2>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-123arm hidden-do98mh">
                    <div
                      className="framer-j85i0t reveal"
                      data-framer-name="Scale faster"
                      data-framer-component-type="RichTextContainer"
                    >
                      <h2 dir="auto" className="framer-text cards-s156">
                        <span className="framer-text cards-s157">
                          Your balance. Everywhere
                        </span>
                        <span className="framer-text cards-s157">
                          <br className="framer-text" />
                        </span>
                        <span className="framer-text cards-s157">you pay.</span>
                      </h2>
                    </div>
                  </div>
                  <div
                    className="framer-piyna1 reveal"
                    data-framer-name="Build rich user connections, spark action with tailored CTAs,\u2028and tap into new revenue channels - all within Discord!"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text cards-s65">
                      Not another card. A direct extension of your global
                      account.
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="framer-146hsal"
                data-border="true"
                data-framer-name="Background video"
              >
                <div
                  className="framer-vrsbyc"
                  data-framer-name="desaturation"
                />
                <div className="framer-1cqgwsa" data-framer-name="cover" />
                <div
                  className="framer-1jue17f"
                  data-framer-name="CHANGE THIS - accent color"
                />
                <div className="framer-11pqigp-container">
                  {/*$*/}
                  <div className="cards-s158">
                    <canvas className="cards-s19" />
                  </div>
                  {/*/$*/}
                </div>
              </div>
              <div className="ssr-variant hidden-do98mh hidden-1u67xp5">
                <div className="framer-ro97c1 cards-s159">
                  <div
                    data-framer-background-image-wrapper="true"
                    className="cards-s28"
                  >
                    <img
                      decoding="async"
                      loading="lazy"
                      width="5760"
                      height="1743"
                      sizes="(min-width: 1440px) 100vw, (min-width: 810px) and (max-width: 1439.98px) 981.4802px, (max-width: 809.98px) 370px"
                      srcSet="/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 512w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 1024w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 2048w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 4096w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 5760w"
                      src="/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png"
                      alt=""
                      className="cards-s29"
                    />
                  </div>
                </div>
              </div>
              <div className="ssr-variant hidden-123arm hidden-1u67xp5">
                <div className="framer-ro97c1 cards-s160">
                  <div
                    data-framer-background-image-wrapper="true"
                    className="cards-s28"
                  >
                    <img
                      decoding="async"
                      loading="lazy"
                      width="5760"
                      height="1743"
                      sizes="(min-width: 1440px) 100vw, (min-width: 810px) and (max-width: 1439.98px) 981.4802px, (max-width: 809.98px) 370px"
                      srcSet="/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 512w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 1024w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 2048w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 4096w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 5760w"
                      src="/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png"
                      alt=""
                      className="cards-s29"
                    />
                  </div>
                </div>
              </div>
              <div className="ssr-variant hidden-123arm hidden-do98mh">
                <div className="framer-ro97c1">
                  <div
                    data-framer-background-image-wrapper="true"
                    className="cards-s28"
                  >
                    <img
                      decoding="async"
                      loading="lazy"
                      width="5760"
                      height="1743"
                      sizes="(min-width: 1440px) 100vw, (min-width: 810px) and (max-width: 1439.98px) 981.4802px, (max-width: 809.98px) 370px"
                      srcSet="/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 512w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 1024w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 2048w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 4096w,/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png 5760w"
                      src="/framerusercontent.com/images/VggiAaVqXnlI7E4dFGq1R1p6YcE.png"
                      alt=""
                      className="cards-s29"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="ssr-variant hidden-do98mh hidden-1u67xp5">
              <div className="framer-14vqdep-container" id="14vqdep">
                <section
                  className="framer-FEVxH framer-5t5vi4 framer-v-5t5vi4 cards-s161"
                  data-framer-name="Desktop"
                  data-highlight="true"
                  tabIndex={0}
                >
                  <div
                    className="framer-wyhoy3 cards-s162"
                    data-framer-name="CTA Wrapper"
                  >
                    <div className="framer-ri909w" data-framer-name="Container">
                      <div
                        className="framer-1hvn8hd cards-s163 reveal"
                        data-framer-name="Scale faster"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h2 dir="auto" className="framer-text cards-s164">
                          &nbsp;Start spending globally
                        </h2>
                      </div>
                      <div
                        className="framer-1qfgjr9 cards-s165 reveal"
                        data-framer-name="Powerful, self-serve product and growth analytics to help you convert, engage, and retain more."
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s166">
                          Use your balance anywhere, instantly.
                        </p>
                      </div>
                    </div>
                    {/*$*/}
                    <a
                      className="framer-1pnv17b framer-i8tgb5 cards-s85"
                      data-border="true"
                      data-framer-name="Button"
                      href="https://onelink.to/wch597"
                      target="_blank"
                      rel="noopener"
                    >
                      <div
                        className="framer-11c8v60 cards-s86 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s87">
                          Get your card
                        </p>
                      </div>
                    </a>
                    {/*/$*/}
                  </div>
                </section>
              </div>
            </div>
            <div className="ssr-variant hidden-123arm hidden-1u67xp5">
              <div className="framer-14vqdep-container" id="14vqdep">
                <section
                  className="framer-FEVxH framer-5t5vi4 framer-v-dpsohg cards-s161"
                  data-framer-name="Tablet"
                  data-highlight="true"
                  tabIndex={0}
                >
                  <div
                    className="framer-wyhoy3 cards-s162"
                    data-framer-name="CTA Wrapper"
                  >
                    <div className="framer-ri909w" data-framer-name="Container">
                      <div
                        className="framer-1hvn8hd cards-s163 reveal"
                        data-framer-name="Scale faster"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h2 dir="auto" className="framer-text cards-s164">
                          &nbsp;Start spending globally
                        </h2>
                      </div>
                      <div
                        className="framer-1qfgjr9 cards-s165 reveal"
                        data-framer-name="Powerful, self-serve product and growth analytics to help you convert, engage, and retain more."
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s166">
                          Use your balance anywhere, instantly.
                        </p>
                      </div>
                    </div>
                    {/*$*/}
                    <a
                      className="framer-1pnv17b framer-i8tgb5 cards-s85"
                      data-border="true"
                      data-framer-name="Button"
                      href="https://onelink.to/wch597"
                      target="_blank"
                      rel="noopener"
                    >
                      <div
                        className="framer-11c8v60 cards-s86 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s87">
                          Get your card
                        </p>
                      </div>
                    </a>
                    {/*/$*/}
                  </div>
                </section>
              </div>
            </div>
            <div className="ssr-variant hidden-123arm hidden-do98mh">
              <div className="framer-14vqdep-container" id="14vqdep">
                <section
                  className="framer-FEVxH framer-5t5vi4 framer-v-1obuwcu cards-s167"
                  data-framer-name="Phone"
                  data-highlight="true"
                  tabIndex={0}
                >
                  <div
                    className="framer-wyhoy3 cards-s162"
                    data-framer-name="CTA Wrapper"
                  >
                    <div className="framer-ri909w" data-framer-name="Container">
                      <div
                        className="framer-1hvn8hd cards-s163 reveal"
                        data-framer-name="Scale faster"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h2 dir="auto" className="framer-text cards-s168">
                          &nbsp;Start spending globally
                        </h2>
                      </div>
                      <div
                        className="framer-1qfgjr9 cards-s165 reveal"
                        data-framer-name="Powerful, self-serve product and growth analytics to help you convert, engage, and retain more."
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s166">
                          Use your balance anywhere, instantly.
                        </p>
                      </div>
                    </div>
                    {/*$*/}
                    <a
                      className="framer-1pnv17b framer-i8tgb5 cards-s85"
                      data-border="true"
                      data-framer-name="Button"
                      href="https://onelink.to/wch597"
                      target="_blank"
                      rel="noopener"
                    >
                      <div
                        className="framer-11c8v60 cards-s86 reveal"
                        data-framer-name="Label"
                        data-framer-component-type="RichTextContainer"
                      >
                        <p dir="auto" className="framer-text cards-s87">
                          Get your card
                        </p>
                      </div>
                    </a>
                    {/*/$*/}
                  </div>
                </section>
              </div>
            </div>
            <div className="ssr-variant hidden-do98mh hidden-1u67xp5">
              <div className="framer-cgc29u-container">
                <div
                  className="framer-60Kb5 framer-1rilglu framer-v-1rilglu cards-s169"
                  data-framer-name="Desktop"
                >
                  <div
                    className="framer-14npji9"
                    data-framer-name="Frame 33595"
                  >
                    <div className="framer-1nbyyte">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1ick3k6 cards-s104"
                        aria-hidden="true"
                      >
                        <div className="svgContainer cards-s59">
                          <svg viewBox="0 0 699 196" className="cards-s60">
                            <use href="#svg-1137231340_6119" />
                          </svg>
                        </div>
                      </div>
                      <div className="framer-6st60f">
                        <div
                          className="framer-9nwe5k cards-s170 reveal"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s171">
                            <strong className="framer-text">
                              One account for all your currencies{' '}
                              <br className="framer-text" />
                            </strong>
                            Manage, send, and receive money globally
                          </p>
                        </div>
                      </div>
                      <div
                        className="framer-1d0k7xa"
                        data-framer-name="ICO Logo_Blue"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="cards-s28"
                        >
                          <img
                            decoding="async"
                            width="1440"
                            height="1440"
                            sizes="(min-width: 1440px) 60px, (min-width: 810px) and (max-width: 1439.98px) 60px, (max-width: 809.98px) 60px"
                            srcSet="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 512w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1024w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1440w"
                            src="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png"
                            alt=""
                            className="cards-s29"
                          />
                        </div>
                      </div>
                      <div className="scallop-compliance-note">
                        <p>
                          This website is managed by Scallop Pvt Limited, Sri Lanka.
                          <br />
                          Scallop does not provide services in the countries listed on
                          its{' '}
                          <a
                            href="https://docs.scallopx.com/details/onboarding-regions"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Prohibited Countries list
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                    <div
                      className="framer-c50yne"
                      data-framer-name="Frame 33594"
                    >
                      <div className="framer-5dec6k" data-framer-name="Menu">
                        <div
                          className="framer-nmphiu cards-s172 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s173">
                            Programs
                          </p>
                        </div>
                        <div className="framer-1odpfed" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1tz9h0j-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/ambassador"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/ambassador"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Ambassador
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                      <div className="framer-a8htgk" data-framer-name="Menu">
                        <div
                          className="framer-1rugbzn cards-s172 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s173">
                            Products
                          </p>
                        </div>
                        <div className="framer-1oe4fxe" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-153yqwe-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/accounts"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/accounts"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Accounts
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1di1zw4-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/cards"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/cards"
                                    data-framer-page-link-current="true"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Cards
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-165n05h-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-pay"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-pay"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Pay
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1ddhrxh-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-plus"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-plus"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Plus
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-nsl31k-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://blog.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://blog.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Blogs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                      <div className="framer-bcfdyi" data-framer-name="Menu">
                        <div
                          className="framer-cv5tks cards-s172 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s173">
                            Support
                          </p>
                        </div>
                        <div className="framer-16j5a70" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1apiy1v-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/support"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/support"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Contact Us
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-650678-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/helpdesk"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/helpdesk"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    FAQs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-1935128-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://docs.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://docs.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Docs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="framer-1l4w042"
                    data-framer-name="Frame 33831"
                  >
                    <div className="framer-jk8g5w" data-framer-name="Link">
                      <div className="framer-80yh0c">
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="ic:baseline-copyright"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-m3skko cards-s104"
                          aria-hidden="true"
                        >
                          <div className="svgContainer cards-s59">
                            <svg viewBox="0 0 20 20" className="cards-s60">
                              <use href="#svg-1839680863_948" />
                            </svg>
                          </div>
                        </div>
                        <div
                          className="framer-hv1cvb cards-s175 reveal"
                          data-framer-name="Item"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s176">
                            2026 Scallop Group. All rights reserved.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-1c28m8b">
                      {/*$*/}
                      <a
                        data-framer-component-type="SVG"
                        as="a"
                        data-framer-name="X"
                        href="https://x.com/Scallopcom"
                        target="_blank"
                        rel="noopener"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-dziac9 framer-1d3y7cu cards-s177"
                        aria-hidden="true"
                      />
                      {/*/$*/}
                      {/*$*/}
                      <a
                        className="framer-n9nhin framer-1d3y7cu"
                        data-framer-name="LinkdIn"
                        href="https://www.linkedin.com/company/scallopcom"
                        target="_blank"
                        rel="noopener"
                      >
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="linkd"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-1ggemab cards-s178"
                          aria-hidden="true"
                        />
                      </a>
                      {/*/$*/}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="ssr-variant hidden-123arm hidden-1u67xp5">
              <div className="framer-cgc29u-container">
                <div
                  className="framer-60Kb5 framer-1rilglu framer-v-183e6ee cards-s169"
                  data-framer-name="Tablet"
                >
                  <div
                    className="framer-14npji9"
                    data-framer-name="Frame 33595"
                  >
                    <div className="framer-1nbyyte">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1ick3k6 cards-s104"
                        aria-hidden="true"
                      >
                        <div className="svgContainer cards-s59">
                          <svg viewBox="0 0 699 196" className="cards-s60">
                            <use href="#svg-1137231340_6119" />
                          </svg>
                        </div>
                      </div>
                      <div className="framer-6st60f">
                        <div
                          className="framer-9nwe5k cards-s170 reveal"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s171">
                            <strong className="framer-text">
                              One account for all your currencies{' '}
                              <br className="framer-text" />
                            </strong>
                            Manage, send, and receive money globally
                          </p>
                        </div>
                      </div>
                      <div
                        className="framer-1d0k7xa"
                        data-framer-name="ICO Logo_Blue"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="cards-s28"
                        >
                          <img
                            decoding="async"
                            width="1440"
                            height="1440"
                            sizes="(min-width: 1440px) 60px, (min-width: 810px) and (max-width: 1439.98px) 60px, (max-width: 809.98px) 60px"
                            srcSet="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 512w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1024w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1440w"
                            src="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png"
                            alt=""
                            className="cards-s29"
                          />
                        </div>
                      </div>
                      <div className="scallop-compliance-note">
                        <p>
                          This website is managed by Scallop Pvt Limited, Sri Lanka.
                          <br />
                          Scallop does not provide services in the countries listed on
                          its{' '}
                          <a
                            href="https://docs.scallopx.com/details/onboarding-regions"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Prohibited Countries list
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                    <div
                      className="framer-c50yne"
                      data-framer-name="Frame 33594"
                    >
                      <div className="framer-5dec6k" data-framer-name="Menu">
                        <div
                          className="framer-nmphiu cards-s172 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s173">
                            Programs
                          </p>
                        </div>
                        <div className="framer-1odpfed" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1tz9h0j-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/ambassador"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/ambassador"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Ambassador
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                      <div className="framer-a8htgk" data-framer-name="Menu">
                        <div
                          className="framer-1rugbzn cards-s172 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s173">
                            Products
                          </p>
                        </div>
                        <div className="framer-1oe4fxe" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-153yqwe-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/accounts"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/accounts"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Accounts
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1di1zw4-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/cards"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/cards"
                                    data-framer-page-link-current="true"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Cards
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-165n05h-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-pay"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-pay"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Pay
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1ddhrxh-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-plus"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-plus"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Plus
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-nsl31k-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://blog.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://blog.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Blogs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                      <div className="framer-bcfdyi" data-framer-name="Menu">
                        <div
                          className="framer-cv5tks cards-s172 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s173">
                            Support
                          </p>
                        </div>
                        <div className="framer-16j5a70" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1apiy1v-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/support"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/support"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Contact Us
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-650678-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/helpdesk"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/helpdesk"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    FAQs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-1935128-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://docs.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://docs.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Docs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="framer-1l4w042"
                    data-framer-name="Frame 33831"
                  >
                    <div className="framer-jk8g5w" data-framer-name="Link">
                      <div className="framer-80yh0c">
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="ic:baseline-copyright"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-m3skko cards-s104"
                          aria-hidden="true"
                        >
                          <div className="svgContainer cards-s59">
                            <svg viewBox="0 0 20 20" className="cards-s60">
                              <use href="#svg-1839680863_948" />
                            </svg>
                          </div>
                        </div>
                        <div
                          className="framer-hv1cvb cards-s175 reveal"
                          data-framer-name="Item"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s176">
                            2026 Scallop Group. All rights reserved.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-1c28m8b">
                      {/*$*/}
                      <a
                        data-framer-component-type="SVG"
                        as="a"
                        data-framer-name="X"
                        href="https://x.com/Scallopcom"
                        target="_blank"
                        rel="noopener"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-dziac9 framer-1d3y7cu cards-s177"
                        aria-hidden="true"
                      />
                      {/*/$*/}
                      {/*$*/}
                      <a
                        className="framer-n9nhin framer-1d3y7cu"
                        data-framer-name="LinkdIn"
                        href="https://www.linkedin.com/company/scallopcom"
                        target="_blank"
                        rel="noopener"
                      >
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="linkd"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-1ggemab cards-s178"
                          aria-hidden="true"
                        />
                      </a>
                      {/*/$*/}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="ssr-variant hidden-123arm hidden-do98mh">
              <div className="framer-cgc29u-container">
                <div
                  className="framer-60Kb5 framer-1rilglu framer-v-ltf6f8 cards-s169"
                  data-framer-name="Mobile"
                >
                  <div
                    className="framer-14npji9"
                    data-framer-name="Frame 33595"
                  >
                    <div className="framer-1nbyyte">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1ick3k6 cards-s104"
                        aria-hidden="true"
                      >
                        <div className="svgContainer cards-s59">
                          <svg viewBox="0 0 699 196" className="cards-s60">
                            <use href="#svg-1137231340_6119" />
                          </svg>
                        </div>
                      </div>
                      <div className="framer-6st60f">
                        <div
                          className="framer-9nwe5k cards-s170 reveal"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s171">
                            <strong className="framer-text">
                              One account for all your currencies{' '}
                              <br className="framer-text" />
                            </strong>
                            Manage, send, and receive money globally
                          </p>
                        </div>
                      </div>
                      <div
                        className="framer-1d0k7xa"
                        data-framer-name="ICO Logo_Blue"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="cards-s28"
                        >
                          <img
                            decoding="async"
                            width="1440"
                            height="1440"
                            sizes="(min-width: 1440px) 60px, (min-width: 810px) and (max-width: 1439.98px) 60px, (max-width: 809.98px) 60px"
                            srcSet="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 512w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1024w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1440w"
                            src="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png"
                            alt=""
                            className="cards-s29"
                          />
                        </div>
                      </div>
                      <div className="scallop-compliance-note">
                        <p>
                          This website is managed by Scallop Pvt Limited, Sri Lanka.
                          <br />
                          Scallop does not provide services in the countries listed on
                          its{' '}
                          <a
                            href="https://docs.scallopx.com/details/onboarding-regions"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Prohibited Countries list
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                    <div
                      className="framer-c50yne"
                      data-framer-name="Frame 33594"
                    >
                      <div className="framer-5dec6k" data-framer-name="Menu">
                        <div
                          className="framer-nmphiu cards-s172 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s173">
                            Programs
                          </p>
                        </div>
                        <div className="framer-1odpfed" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1tz9h0j-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/ambassador"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/ambassador"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Ambassador
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                      <div className="framer-a8htgk" data-framer-name="Menu">
                        <div
                          className="framer-1rugbzn cards-s172 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s173">
                            Products
                          </p>
                        </div>
                        <div className="framer-1oe4fxe" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-153yqwe-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/accounts"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/accounts"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Accounts
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1di1zw4-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/cards"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/cards"
                                    data-framer-page-link-current="true"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Cards
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-165n05h-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-pay"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-pay"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Pay
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1ddhrxh-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-plus"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-plus"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Plus
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-nsl31k-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://blog.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://blog.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Blogs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                      <div className="framer-bcfdyi" data-framer-name="Menu">
                        <div
                          className="framer-cv5tks cards-s172 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s173">
                            Support
                          </p>
                        </div>
                        <div className="framer-16j5a70" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1apiy1v-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/support"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/support"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Contact Us
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-650678-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/helpdesk"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/helpdesk"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    FAQs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-1935128-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://docs.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb cards-s172 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text cards-s174">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://docs.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Docs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="framer-1l4w042"
                    data-framer-name="Frame 33831"
                  >
                    <div className="framer-jk8g5w" data-framer-name="Link">
                      <div className="framer-80yh0c">
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="ic:baseline-copyright"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-m3skko cards-s104"
                          aria-hidden="true"
                        >
                          <div className="svgContainer cards-s59">
                            <svg viewBox="0 0 20 20" className="cards-s60">
                              <use href="#svg-1839680863_948" />
                            </svg>
                          </div>
                        </div>
                        <div
                          className="framer-hv1cvb cards-s175 reveal"
                          data-framer-name="Item"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text cards-s176">
                            2026 Scallop Group. All rights reserved.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-1c28m8b">
                      {/*$*/}
                      <a
                        data-framer-component-type="SVG"
                        as="a"
                        data-framer-name="X"
                        href="https://x.com/Scallopcom"
                        target="_blank"
                        rel="noopener"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-dziac9 framer-1d3y7cu cards-s177"
                        aria-hidden="true"
                      />
                      {/*/$*/}
                      {/*$*/}
                      <a
                        className="framer-n9nhin framer-1d3y7cu"
                        data-framer-name="LinkdIn"
                        href="https://www.linkedin.com/company/scallopcom"
                        target="_blank"
                        rel="noopener"
                      >
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="linkd"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-1ggemab cards-s178"
                          aria-hidden="true"
                        />
                      </a>
                      {/*/$*/}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="overlay" />
        {/*/$*/}
      </>
    </div>
  );
}

export default Cards;
